
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import ChatAdvisor, { clearTemplateChatHistory } from './ChatAdvisor';
import LessonIframe from './LessonIframe';
import CodePreview from './CodePreview';
import { injectNavGuard } from './navGuard';
import InlineEditor from './InlineEditor';
import ReviewSidebar from './ReviewSidebar';
import DistributorDashboard from './DistributorDashboard';
import AdminPage from './AdminPage';
import MyTasks from './MyTasks';
import LearnerView from './LearnerView';
const STORAGE_KEY = 'daily-huddle-board';
const LEGACY_STORAGE_KEY = 'daily-huddle-lesson';
const OPENAI_MODEL_ID = 'gpt-5.4';
const LEGACY_OPENAI_MODELS = ['gpt5', 'openai', 'gpt-5.1', 'gpt-5.1-codex', 'gpt-5.1-codex-max', 'gpt-5.4-mini'];
const APP_AUTH_STORAGE_KEY = 'app_auth_session';

// ─── Standalone Cost Monitor page (opens in new tab at /monitor-cost) ────────
function MonitorCostPage() {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const API_URL = process.env.REACT_APP_API_URL || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('lesson');
  const [page, setPage] = useState(1);
  const [expandedLessons, setExpandedLessons] = useState(new Set());
  const PAGE_SIZE = 100;

  const toggleLesson = (id) => setExpandedLessons(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const expandAll = () => setExpandedLessons(new Set(lessonData.map(l=>l.template_id)));
  const collapseAll = () => setExpandedLessons(new Set());

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) { loginWithRedirect(); return; }
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${API_URL}/monitor/costs`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [isLoading, isAuthenticated]); // eslint-disable-line

  // Lesson view: filter lessons directly (grouped render)
  const lessonData = useMemo(() => {
    if (!data) return [];
    const lessons = data.lessons || [];
    if (!search.trim()) return lessons;
    const q = search.toLowerCase();
    return lessons.filter(l =>
      (l.lesson_name||'').toLowerCase().includes(q) ||
      (l.template_id||'').toLowerCase().includes(q) ||
      (l.llm_entries||[]).some(e=>(e.model||'').toLowerCase().includes(q))
    );
  }, [data, search]);

  // Other views: flat aggregate rows
  const flatRows = useMemo(() => {
    if (!data || view === 'lesson') return [];
    const lessons = data.lessons || [];
    if (view === 'model') {
      const m = {};
      lessons.forEach(l => {
        (l.llm_entries||[]).forEach(e => {
          const k = e.model||'unknown';
          if (!m[k]) m[k]={key:k,name:k,date:e.ts,model:k,input_tokens:0,output_tokens:0,images:0,llm_cost:0,image_cost:0,total_cost:0};
          m[k].input_tokens+=e.input_tokens||0; m[k].output_tokens+=e.output_tokens||0;
          m[k].llm_cost+=e.cost||0; m[k].total_cost+=e.cost||0;
        });
        (l.image_entries||[]).forEach(e => {
          const k = e.model||'unknown-img';
          if (!m[k]) m[k]={key:k,name:k,date:e.ts,model:k,input_tokens:0,output_tokens:0,images:0,llm_cost:0,image_cost:0,total_cost:0};
          m[k].images+=e.image_count||0; m[k].image_cost+=e.cost||0; m[k].total_cost+=e.cost||0;
        });
      });
      return Object.values(m).sort((a,b)=>b.total_cost-a.total_cost);
    }
    const groupKey = view==='date' ? d=>d.substring(0,10) : view==='month' ? d=>d.substring(0,7) : d=>d.substring(0,4);
    const grp = {};
    lessons.forEach(l => {
      const k = l.created_at ? groupKey(l.created_at) : 'unknown';
      if (!grp[k]) grp[k]={key:k,name:k,date:k,model:'—',input_tokens:0,output_tokens:0,images:0,llm_cost:0,image_cost:0,total_cost:0,lessons:0};
      grp[k].input_tokens+=l.total_input_tokens||0; grp[k].output_tokens+=l.total_output_tokens||0;
      grp[k].images+=l.total_images||0; grp[k].image_cost+=l.image_cost||0;
      grp[k].llm_cost+=(l.total_cost||0)-(l.image_cost||0); grp[k].total_cost+=l.total_cost||0; grp[k].lessons++;
    });
    return Object.values(grp).sort((a,b)=>b.key.localeCompare(a.key));
  }, [data, view]);

  const filteredFlatRows = useMemo(() => {
    if (!search.trim()) return flatRows;
    const q = search.toLowerCase();
    return flatRows.filter(r=>(r.name||'').toLowerCase().includes(q)||(r.model||'').toLowerCase().includes(q));
  }, [flatRows, search]);

  const activeList = view === 'lesson' ? lessonData : filteredFlatRows;
  const totalPages = Math.max(1, Math.ceil(activeList.length/PAGE_SIZE));
  const pageItems = activeList.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  const totals = useMemo(() => {
    if (view === 'lesson') {
      return lessonData.reduce((a,l) => ({
        input_tokens: a.input_tokens+(l.total_input_tokens||0),
        output_tokens: a.output_tokens+(l.total_output_tokens||0),
        images: a.images+(l.total_images||0),
        llm_cost: a.llm_cost+(l.total_cost||0)-(l.image_cost||0),
        image_cost: a.image_cost+(l.image_cost||0),
        total_cost: a.total_cost+(l.total_cost||0),
      }), {input_tokens:0,output_tokens:0,images:0,llm_cost:0,image_cost:0,total_cost:0});
    }
    return filteredFlatRows.reduce((a,r)=>({
      input_tokens:a.input_tokens+(r.input_tokens||0), output_tokens:a.output_tokens+(r.output_tokens||0),
      images:a.images+(r.images||0), llm_cost:a.llm_cost+(r.llm_cost||0),
      image_cost:a.image_cost+(r.image_cost||0), total_cost:a.total_cost+(r.total_cost||0),
    }),{input_tokens:0,output_tokens:0,images:0,llm_cost:0,image_cost:0,total_cost:0});
  }, [view, lessonData, filteredFlatRows]);

  const S = {
    page:{height:'100vh',display:'flex',flexDirection:'column',background:'#0f1117',color:'#e5e7eb',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',overflow:'hidden'},
    hdr:{background:'#161b2e',borderBottom:'1px solid #2d3348',padding:'14px 24px',display:'flex',alignItems:'center',gap:'12px',flexShrink:0},
    card:()=>({background:'#1a2035',border:'1px solid #2d3348',borderRadius:'8px',padding:'10px 16px',textAlign:'center',minWidth:'120px'}),
    vBtn:(a)=>({padding:'6px 14px',borderRadius:'6px',border:'1px solid',borderColor:a?'#6366f1':'#374151',background:a?'#6366f1':'transparent',color:a?'#fff':'#9ca3af',cursor:'pointer',fontSize:'13px',fontWeight:a?600:400}),
    th:{padding:'10px 12px',textAlign:'left',fontWeight:600,color:'#9ca3af',borderBottom:'2px solid #2d3348',whiteSpace:'nowrap',background:'#161b2e',position:'sticky',top:0,zIndex:2},
    td:{padding:'9px 12px',borderBottom:'1px solid #1e2740',verticalAlign:'middle'},
    pBtn:(d)=>({padding:'5px 11px',borderRadius:'5px',border:'1px solid #374151',background:d?'transparent':'#2d3348',color:d?'#4b5563':'#e5e7eb',cursor:d?'not-allowed':'pointer',fontSize:'13px'}),
    inp:{background:'#1a2035',border:'1px solid #374151',borderRadius:'6px',padding:'7px 12px',color:'#e5e7eb',fontSize:'13px',outline:'none',width:'260px'},
    scroll:{flex:1,overflow:'auto'},
  };

  const centerStyle = {height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0f1117',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'};
  if (isLoading||loading) return <div style={centerStyle}><div style={{color:'#6b7280',fontSize:'16px'}}>Loading cost data…</div></div>;
  if (error) return <div style={centerStyle}><div style={{color:'#ef4444',textAlign:'center'}}><div style={{fontSize:'20px',marginBottom:'8px'}}>Error</div><div style={{fontSize:'13px',color:'#9ca3af'}}>{error}</div></div></div>;

  const pagesArr = [];
  const pStart = Math.max(1, page-3), pEnd = Math.min(totalPages, pStart+6);
  for(let i=pStart;i<=pEnd;i++) pagesArr.push(i);

  const Pagination = () => (
    <>
      <span style={{fontSize:'12px',color:'#6b7280'}}>Page {page}/{totalPages}</span>
      <button style={S.pBtn(page<=1)} disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹</button>
      {pagesArr.map(p=>(
        <button key={p} style={{...S.pBtn(false),background:p===page?'#6366f1':'#1e2740',color:p===page?'#fff':'#d1d5db',minWidth:'32px',textAlign:'center'}} onClick={()=>setPage(p)}>{p}</button>
      ))}
      <button style={S.pBtn(page>=totalPages)} disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>›</button>
    </>
  );

  // ── Lesson grouped table ──────────────────────────────────────────────────
  const renderLessonTable = () => {
    const RUN_COLOR = {main:'#6366f1',visual_fix:'#f59e0b',imgfix:'#f59e0b',qa:'#10b981'};
    return (
      <div style={S.scroll}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',minWidth:'900px'}}>
          <thead>
            <tr>
              <th style={{...S.th,width:'36px'}}>#</th>
              <th style={S.th}>Lesson</th>
              <th style={S.th}>Run</th>
              <th style={S.th}>Model</th>
              <th style={{...S.th,textAlign:'right'}}>Input Tokens</th>
              <th style={{...S.th,textAlign:'right'}}>Output Tokens</th>
              <th style={{...S.th,textAlign:'right'}}>Images</th>
              <th style={{...S.th,textAlign:'right'}}>LLM Cost</th>
              <th style={{...S.th,textAlign:'right'}}>Img Cost</th>
              <th style={{...S.th,textAlign:'right'}}>Total</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && (
              <tr><td colSpan={10} style={{...S.td,textAlign:'center',color:'#6b7280',padding:'48px'}}>No cost data yet. Generate a lesson to start tracking.</td></tr>
            )}
            {pageItems.map((l, li) => {
              const llmEntries = l.llm_entries || [];
              const imgEntries = l.image_entries || [];
              const lessonLlmCost = (l.total_cost||0)-(l.image_cost||0);
              const lessonNum = (page-1)*PAGE_SIZE+li+1;
              const tid = l.template_id||li;
              const isOpen = expandedLessons.has(tid);
              const entryCount = llmEntries.length + imgEntries.length;
              return (
                <React.Fragment key={tid}>
                  {/* Lesson header row — click to expand/collapse */}
                  <tr
                    onClick={()=>toggleLesson(tid)}
                    style={{background:'#1a2035',borderTop:'2px solid #374151',cursor:'pointer'}}
                    title={isOpen?'Click to collapse':'Click to expand details'}
                  >
                    <td style={{...S.td,color:'#6b7280',verticalAlign:'middle'}}>{lessonNum}</td>
                    <td style={{...S.td,verticalAlign:'middle'}} colSpan={3}>
                      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                        <span style={{fontSize:'16px',color:'#6366f1',transition:'transform 0.15s',display:'inline-block',transform:isOpen?'rotate(90deg)':'rotate(0deg)'}}>›</span>
                        <div>
                          <div style={{fontWeight:700,color:'#fff',fontSize:'14px'}}>{l.lesson_name||l.template_id}</div>
                          <div style={{fontSize:'11px',color:'#4b5563',marginTop:'1px'}}>{l.template_id} · {l.created_at?new Date(l.created_at).toLocaleString():'—'} · <span style={{color:'#6366f1'}}>{entryCount} run{entryCount!==1?'s':''}</span></div>
                        </div>
                      </div>
                    </td>
                    <td style={{...S.td,textAlign:'right',color:'#60a5fa',fontWeight:600}}>{(l.total_input_tokens||0).toLocaleString()}</td>
                    <td style={{...S.td,textAlign:'right',color:'#a78bfa',fontWeight:600}}>{(l.total_output_tokens||0).toLocaleString()}</td>
                    <td style={{...S.td,textAlign:'right',color:'#34d399',fontWeight:600}}>{(l.total_images||0).toLocaleString()}</td>
                    <td style={{...S.td,textAlign:'right',fontWeight:600}}>${Math.max(0,lessonLlmCost).toFixed(4)}</td>
                    <td style={{...S.td,textAlign:'right',color:'#10b981',fontWeight:600}}>${(l.image_cost||0).toFixed(4)}</td>
                    <td style={{...S.td,textAlign:'right',fontWeight:700,color:'#ef4444',fontSize:'15px'}}>${(l.total_cost||0).toFixed(4)}</td>
                  </tr>
                  {/* Sub-rows: only render when expanded */}
                  {isOpen && llmEntries.map((e,ei) => {
                    const rl = e.run||e.run_label||'main';
                    const rc = RUN_COLOR[rl]||'#9ca3af';
                    return (
                      <tr key={`llm-${ei}`} style={{background:'#0d1117'}}>
                        <td style={{...S.td,borderBottom:'1px solid #161b2e'}}/>
                        <td style={{...S.td,paddingLeft:'40px',borderBottom:'1px solid #161b2e',color:'#9ca3af',fontSize:'12px'}}>
                          {e.ts?new Date(e.ts).toLocaleString():'—'}
                        </td>
                        <td style={{...S.td,borderBottom:'1px solid #161b2e'}}>
                          <span style={{background:rc+'22',color:rc,padding:'2px 8px',borderRadius:'4px',fontSize:'12px',fontWeight:600}}>{rl}</span>
                        </td>
                        <td style={{...S.td,borderBottom:'1px solid #161b2e'}}>
                          <span style={{background:'#1e1b4b',color:'#a5b4fc',padding:'2px 8px',borderRadius:'4px',fontSize:'12px'}}>{e.model||'—'}</span>
                        </td>
                        <td style={{...S.td,textAlign:'right',color:'#60a5fa',borderBottom:'1px solid #161b2e'}}>{(e.input_tokens||0).toLocaleString()}</td>
                        <td style={{...S.td,textAlign:'right',color:'#a78bfa',borderBottom:'1px solid #161b2e'}}>{(e.output_tokens||0).toLocaleString()}</td>
                        <td style={{...S.td,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #161b2e'}}>—</td>
                        <td style={{...S.td,textAlign:'right',borderBottom:'1px solid #161b2e'}}>${(e.cost||0).toFixed(4)}</td>
                        <td style={{...S.td,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #161b2e'}}>—</td>
                        <td style={{...S.td,textAlign:'right',color:'#fbbf24',borderBottom:'1px solid #161b2e'}}>${(e.cost||0).toFixed(4)}</td>
                      </tr>
                    );
                  })}
                  {isOpen && imgEntries.map((e,ei) => (
                    <tr key={`img-${ei}`} style={{background:'#0d1117'}}>
                      <td style={{...S.td,borderBottom:'1px solid #161b2e'}}/>
                      <td style={{...S.td,paddingLeft:'40px',borderBottom:'1px solid #161b2e',color:'#9ca3af',fontSize:'12px'}}>
                        {e.ts?new Date(e.ts).toLocaleString():'—'}
                      </td>
                      <td style={{...S.td,borderBottom:'1px solid #161b2e'}}>
                        <span style={{background:'#10b98122',color:'#10b981',padding:'2px 8px',borderRadius:'4px',fontSize:'12px',fontWeight:600}}>images</span>
                      </td>
                      <td style={{...S.td,borderBottom:'1px solid #161b2e'}}>
                        <span style={{background:'#064e3b',color:'#6ee7b7',padding:'2px 8px',borderRadius:'4px',fontSize:'12px'}}>{e.model||'image'}</span>
                      </td>
                      <td style={{...S.td,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #161b2e'}}>—</td>
                      <td style={{...S.td,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #161b2e'}}>—</td>
                      <td style={{...S.td,textAlign:'right',color:'#34d399',borderBottom:'1px solid #161b2e'}}>{(e.image_count||0)}</td>
                      <td style={{...S.td,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #161b2e'}}>—</td>
                      <td style={{...S.td,textAlign:'right',color:'#10b981',borderBottom:'1px solid #161b2e'}}>${(e.cost||0).toFixed(4)}</td>
                      <td style={{...S.td,textAlign:'right',color:'#10b981',borderBottom:'1px solid #161b2e'}}>${(e.cost||0).toFixed(4)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          {pageItems.length>0 && (
            <tfoot>
              <tr style={{background:'#1e2740',fontWeight:700}}>
                <td colSpan={4} style={{...S.td,color:'#9ca3af',fontSize:'12px',letterSpacing:'0.05em'}}>TOTAL — {lessonData.length} lesson{lessonData.length!==1?'s':''}</td>
                <td style={{...S.td,textAlign:'right',color:'#60a5fa'}}>{totals.input_tokens.toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right',color:'#a78bfa'}}>{totals.output_tokens.toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right',color:'#34d399'}}>{totals.images.toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right'}}>${Math.max(0,totals.llm_cost).toFixed(4)}</td>
                <td style={{...S.td,textAlign:'right',color:'#10b981'}}>${totals.image_cost.toFixed(4)}</td>
                <td style={{...S.td,textAlign:'right',color:'#ef4444'}}>${totals.total_cost.toFixed(4)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  // ── Flat aggregate table (model / date / month / year views) ─────────────
  const renderFlatTable = () => {
    const colLabel = {date:'Date',month:'Month',year:'Year',model:'Model'}[view];
    const showDate = view !== 'model';
    const colSpanLeft = showDate ? 3 : 2;
    return (
      <div style={S.scroll}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
          <thead>
            <tr>
              <th style={{...S.th,width:'40px'}}>#</th>
              <th style={S.th}>{colLabel}</th>
              {showDate && <th style={{...S.th,whiteSpace:'nowrap'}}>Date / Time</th>}
              <th style={{...S.th,textAlign:'right'}}>Input Tokens</th>
              <th style={{...S.th,textAlign:'right'}}>Output Tokens</th>
              <th style={{...S.th,textAlign:'right'}}>Images</th>
              <th style={{...S.th,textAlign:'right'}}>LLM Cost</th>
              <th style={{...S.th,textAlign:'right'}}>Image Cost</th>
              <th style={{...S.th,textAlign:'right'}}>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length===0 && (
              <tr><td colSpan={9} style={{...S.td,textAlign:'center',color:'#6b7280',padding:'48px'}}>No data.</td></tr>
            )}
            {pageItems.map((row,i)=>(
              <tr key={row.key} style={{background:i%2===0?'transparent':'#111827'}}>
                <td style={{...S.td,color:'#6b7280'}}>{(page-1)*PAGE_SIZE+i+1}</td>
                <td style={S.td}>
                  <div style={{fontWeight:500,color:'#e5e7eb'}}>{row.name}</div>
                  {row.lessons!=null && <div style={{fontSize:'11px',color:'#6b7280'}}>{row.lessons} lessons</div>}
                </td>
                {showDate && <td style={{...S.td,color:'#9ca3af',whiteSpace:'nowrap'}}>{row.date?new Date(row.date).toLocaleString():'—'}</td>}
                <td style={{...S.td,textAlign:'right',color:'#60a5fa'}}>{(row.input_tokens||0).toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right',color:'#a78bfa'}}>{(row.output_tokens||0).toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right',color:'#34d399'}}>{(row.images||0).toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right'}}>${Math.max(0,row.llm_cost||0).toFixed(4)}</td>
                <td style={{...S.td,textAlign:'right',color:'#10b981'}}>${(row.image_cost||0).toFixed(4)}</td>
                <td style={{...S.td,textAlign:'right',fontWeight:700,color:'#ef4444'}}>${(row.total_cost||0).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
          {pageItems.length>0 && (
            <tfoot>
              <tr style={{background:'#1e2740',fontWeight:700}}>
                <td colSpan={colSpanLeft} style={{...S.td,color:'#9ca3af',fontSize:'12px',letterSpacing:'0.05em'}}>TOTAL — {filteredFlatRows.length} record{filteredFlatRows.length!==1?'s':''}</td>
                <td style={{...S.td,textAlign:'right',color:'#60a5fa'}}>{totals.input_tokens.toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right',color:'#a78bfa'}}>{totals.output_tokens.toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right',color:'#34d399'}}>{totals.images.toLocaleString()}</td>
                <td style={{...S.td,textAlign:'right'}}>${Math.max(0,totals.llm_cost).toFixed(4)}</td>
                <td style={{...S.td,textAlign:'right',color:'#10b981'}}>${totals.image_cost.toFixed(4)}</td>
                <td style={{...S.td,textAlign:'right',color:'#ef4444'}}>${totals.total_cost.toFixed(4)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <div style={S.hdr}>
        <span style={{fontSize:'18px',fontWeight:700,color:'#fff'}}>Cost Monitor</span>
        <span style={{background:'#065f46',color:'#6ee7b7',padding:'2px 8px',borderRadius:'4px',fontSize:'12px',fontWeight:600}}>{activeList.length} {view==='lesson'?'lessons':'records'}</span>
        <div style={{flex:1}}/>
        <input style={S.inp} placeholder="Search lesson or model…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        <button onClick={()=>window.location.reload()} style={{...S.pBtn(false),marginLeft:'8px'}}>Refresh</button>
      </div>

      {/* Summary bar */}
      <div style={{display:'flex',gap:'10px',padding:'12px 24px',borderBottom:'1px solid #2d3348',overflowX:'auto',flexShrink:0}}>
        {[
          {label:'Total Cost',val:`$${totals.total_cost.toFixed(4)}`,color:'#ef4444'},
          {label:'LLM Cost',val:`$${Math.max(0,totals.llm_cost).toFixed(4)}`,color:'#f59e0b'},
          {label:'Image Cost',val:`$${totals.image_cost.toFixed(4)}`,color:'#10b981'},
          {label:'Input Tokens',val:totals.input_tokens.toLocaleString(),color:'#60a5fa'},
          {label:'Output Tokens',val:totals.output_tokens.toLocaleString(),color:'#a78bfa'},
          {label:'Images',val:totals.images.toLocaleString(),color:'#34d399'},
        ].map(c=>(
          <div key={c.label} style={S.card()}>
            <div style={{fontSize:'11px',color:'#6b7280',marginBottom:'4px'}}>{c.label}</div>
            <div style={{fontSize:'18px',fontWeight:700,color:c.color}}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'10px 24px',borderBottom:'1px solid #2d3348',flexWrap:'wrap',background:'#0f1117',flexShrink:0}}>
        <span style={{fontSize:'12px',color:'#6b7280'}}>View by:</span>
        {['lesson','date','month','year','model'].map(v=>(
          <button key={v} style={S.vBtn(view===v)} onClick={()=>{setView(v);setPage(1);}}>{v.charAt(0).toUpperCase()+v.slice(1)}</button>
        ))}
        {view==='lesson' && (
          <>
            <div style={{width:'1px',height:'20px',background:'#374151',margin:'0 4px'}}/>
            <button style={{...S.pBtn(false),fontSize:'12px',padding:'4px 10px'}} onClick={expandAll}>Expand All</button>
            <button style={{...S.pBtn(false),fontSize:'12px',padding:'4px 10px'}} onClick={collapseAll}>Collapse All</button>
          </>
        )}
        <div style={{flex:1}}/>
        <Pagination/>
      </div>

      {view==='lesson' ? renderLessonTable() : renderFlatTable()}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function App() {
  const iframeRef = useRef(null);
  const lessonFrameRef = useRef(null);
  const menuButtonRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLessonLoading, setIsLessonLoading] = useState(true);
  const [lessonError, setLessonError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateImages, setNewTemplateImages] = useState([]);
  const [newTemplateImagePreviews, setNewTemplateImagePreviews] = useState([]);
  const [newTemplateFile, setNewTemplateFile] = useState(null);
  const [newTemplateFilePath, setNewTemplateFilePath] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [newTemplateModel, setNewTemplateModel] = useState('gpt-5.6-luna'); // Model selection for template creation
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [creatingTemplateName, setCreatingTemplateName] = useState('');
  const [creatingTemplateDescription, setCreatingTemplateDescription] = useState('');
  const templateImageInputRef = useRef(null);
  const templateFileInputRef = useRef(null);
  const createInFlightRef = useRef(false); // prevent double submit (e.g. double-click or Strict Mode)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first confirmation, 2 = second confirmation
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyLessonName, setCopyLessonName] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState(true); // Default to gallery view on load
  const [chatDocked, setChatDocked] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(true);
  const [chatNudge, setChatNudge] = useState(false);
  const [chatTooltip, setChatTooltip] = useState(false);
  const [galleryNudge, setGalleryNudge] = useState(false);
  const [galleryTooltip, setGalleryTooltip] = useState(false);
  const [chatPosition, setChatPosition] = useState({ x: 720, y: 140 });
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [iframeNonce, setIframeNonce] = useState(Date.now());
  const selectedTemplateRef = useRef(null); // track current selection for async create (allow opening other apps while creating)
  const isActivelyGeneratingRef = useRef(false); // true while handleCreate polling loop is running
  const [showDeployConfirmModal, setShowDeployConfirmModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [deploymentUrl, setDeploymentUrl] = useState(null);
  const [existingDeployment, setExistingDeployment] = useState(null);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [apiKeysConfig, setApiKeysConfig] = useState({ providers_set: { google: false, openai: false, anthropic: false, deepseek: false }, enabled_models: [], use_user_keys_only: false });
  const [apiKeyInputs, setApiKeyInputs] = useState({ google: '', openai: '', anthropic: '', deepseek: '' });
  const { isAuthenticated, isLoading: authChecking, getAccessTokenSilently, loginWithRedirect, logout, user } = useAuth0();
  const [isEditMode, setIsEditMode] = useState(false);
  const [newTemplateIsAssessment, setNewTemplateIsAssessment] = useState(false);
  const [newTemplateAssessmentSource, setNewTemplateAssessmentSource] = useState('topic');
  const [newTemplateAssessmentTopic, setNewTemplateAssessmentTopic] = useState('');
  const [newTemplateAssessmentTemplateIds, setNewTemplateAssessmentTemplateIds] = useState(''); // comma-separated string
  const [newTemplateQuestionCount, setNewTemplateQuestionCount] = useState('');
  const [roleReady, setRoleReady] = useState(false)
  const [currentRole, setCurrentRole] = useState(null);
  const [currentToken, setCurrentToken] = useState('');
  const [currentPageId, setCurrentPageId] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const [selectedLessonStatus, setSelectedLessonStatus] = useState(null);
  const [showLearnerAssignModal, setShowLearnerAssignModal] = useState(false);
  const [learnerAssignLesson, setLearnerAssignLesson] = useState(null);
  const [currentLessonRole, setCurrentLessonRole] = useState(null);
  const formatDeployTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };
  const [showInlineAlert, setShowInlineAlert] = useState(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState('');
  const [toasts, setToasts] = useState([]);
  const [showQaModal, setShowQaModal] = useState(false);
  const [qaReportData, setQaReportData] = useState(null);
  const [isQaLoading, setIsQaLoading] = useState(false);
  const [qaRunning, setQaRunning] = useState(false);
  const [fixingIssueId, setFixingIssueId] = useState(null);
  const [fixingTemplateId, setFixingTemplateId] = useState(null);
  const [fixIssueError, setFixIssueError] = useState(null);
  const [batchFixingSection, setBatchFixingSection] = useState(null); // section_key being batch-fixed
  const [batchFixProgress, setBatchFixProgress] = useState(null); // {total, completed}
  const [fixingImprovementId, setFixingImprovementId] = useState(null); // individual improvement being fixed
  const [issuesCollapsed, setIssuesCollapsed] = useState(true); // bottom issues list collapsed by default
  const chatDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const prevGalleryModeRef = useRef(galleryViewMode);
  const chatMountRef = useRef(false);
  const chatEffectRunsRef = useRef(0);
  const infoBanner = "AI-powered lesson building: ask the Lesson Copilot to research a topic, generate an interactive lesson, and iterate on it. Switch lessons with the selector to manage multiple lessons.";

  const inferApiUrl = () => {
    const envUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    if (envUrl) return envUrl;
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:8011';
      }
      return window.location.origin;
    }
    return '';
  };

  const API_URL = inferApiUrl();
  const API_ORIGIN = (() => {
    try {
      return new URL(API_URL, window.location.origin).origin;
    } catch {
      return '';
    }
  })();
  const API_ORIGIN_ALT = API_ORIGIN ? API_ORIGIN.replace('localhost', '127.0.0.1') : '';
  const IFRAME_URL = `${API_URL}/board.html`;
  const buildIframeSrc = (templateId, nonce = null) => {
    const token = nonce ?? Date.now();
    return `${IFRAME_URL}?template=${encodeURIComponent(templateId)}&_t=${token}&preview=true`;
  };
  const BRAND_LOGOS = {
    arbiteria: `${API_URL}/brand_logos/Arbiteria_withTL.png`,
    zero2ai: `${API_URL}/brand_logos/zero2AI._logomain.svg`
  };

  const handleOpenQaFeedback = async () => {
    if (!selectedTemplate) return;
    if (fixingIssueId !== null && fixingTemplateId === selectedTemplate && qaReportData && !qaReportData.error) {
      setShowQaModal(true);
      return;
    }
    setQaReportData(null);
    setIsQaLoading(true);
    setShowQaModal(true);
    try {
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/qa-report`, withAuthFetchOptions());
      const data = await response.json();
      if (data.success && data.report) {
        setQaReportData({ status: data.status, ...data.report });
      } else if (data.status === 'generating') {
        setQaReportData({ status: 'generating' });
        startQaPolling(selectedTemplate);
      } else {
        setQaReportData({ status: data.status, error: data.error || "Report not available yet or evaluation failed." });
      }
    } catch (err) {
      console.error("Error fetching QA report:", err);
      setQaReportData({ status: 'error', error: "Failed to fetch report." });
    } finally {
      setIsQaLoading(false);
    }
  };

    // Poll /qa-report every 5s until the backend finishes and returns status "ready".
  // Max ~3 minutes (36 attempts) before giving up.
  const startQaPolling = (templateId) => {
    const MAX_ATTEMPTS = 36;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateId)}/qa-report`, withAuthFetchOptions());
        const data = await res.json();
        if (data.success && data.report) {
          clearInterval(interval);
          setIsQaLoading(false);
          setQaRunning(false);
          showToast('✅ QA evaluation complete! Click the QA button to view the report.', 'success', 30000);
          setQaReportData({ status: data.status, ...data.report });
          return;
        }
        if (data.status === 'error') {
          // Only stop on an actual error — 'not_found' means the report isn't saved yet
          // (evaluation may be running on a different gunicorn worker)
          clearInterval(interval);
          setIsQaLoading(false);
          setQaRunning(false);
          setQaReportData({ status: data.status, error: data.error || "Evaluation failed." });
          return;
        }
      } catch (_) {
        // Network hiccup — keep polling
      }
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(interval);
        setIsQaLoading(false);
        setQaRunning(false);
        setQaReportData({ status: 'error', error: "Evaluation is taking longer than expected. Please refresh to check." });
      }
    }, 5000);
  };

  const getIframeOrigin = () => {
    const src = iframeRef.current?.src;
    if (!src) return API_ORIGIN || API_ORIGIN_ALT || '*';
    try {
      return new URL(src, window.location.origin).origin;
    } catch {
      return API_ORIGIN || API_ORIGIN_ALT || '*';
    }
  };

  const loadStoredAppSession = () => {
    try {
      const raw = localStorage.getItem(APP_AUTH_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  const persistAppSession = (session) => {
    try {
      if (!session) {
        localStorage.removeItem(APP_AUTH_STORAGE_KEY);
        return;
      }
      localStorage.setItem(APP_AUTH_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // ignore storage errors
    }
  };

  const getAuthSessionToken = () => {
    if (currentToken) return currentToken;
    const session = loadStoredAppSession();
    return typeof session?.session_token === 'string' ? session.session_token.trim() : '';
  };

  const withAuthFetchOptions = (options = {}) => {
    const merged = { ...options, credentials: 'include' };
    const headers = { ...(options.headers || {}) };
    const token = getAuthSessionToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    merged.headers = headers;
    return merged;
  };

  // Generate or retrieve session ID for ownership tracking
  const getSessionId = () => {
    let sessionId = localStorage.getItem('lesson_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('lesson_session_id', sessionId);
    }
    return sessionId;
  };

  const [templateOwnership, setTemplateOwnership] = useState({});

  const syncTemplateOwnershipFromList = (templates = []) => {
    const updates = {};
    (templates || []).forEach((t) => {
      if (!t || !t.id) return;
      if (typeof t.is_owner !== 'boolean' && typeof t.can_edit !== 'boolean') return;
      updates[t.id] = {
        is_owner: typeof t.is_owner === 'boolean' ? t.is_owner : !!t.can_edit,
        can_edit: typeof t.can_edit === 'boolean' ? t.can_edit : !!t.is_owner,
        owner_id: t.owner_id ?? null,
        user_id: t.user_id ?? null,
      };
    });
    if (Object.keys(updates).length > 0) {
      setTemplateOwnership((prev) => ({ ...prev, ...updates }));
    }
  };

  const checkTemplateOwnership = async (templateId) => {
    if (!templateId) return false;

    const sessionId = getSessionId();
    try {
      const response = await fetch(
        `${API_URL}/board/templates/${encodeURIComponent(templateId)}/ownership?owner_id=${encodeURIComponent(sessionId)}`,
        withAuthFetchOptions()
      );
      if (response.ok) {
        const data = await response.json();
        setTemplateOwnership(prev => ({
          ...prev,
          [templateId]: data
        }));
        return data.is_owner;
      }
    } catch (error) {
      console.error('Error checking template ownership:', error);
    }
    return false;
  };

  useEffect(() => {
    document.title = 'LessonGen | AI Lesson Builder';
    // Intentional one-time setup for title
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, isLessonLoading]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_API_AUDIENCE,
          }
        });
        setCurrentToken(token);
        // Extract role from Auth0 token namespace
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const roles = tokenPayload['https://lessongen/roles'] || [];
          if (roles.length === 0) {
            // No role assigned — show error, don't let them in
            setCurrentRole(null);
            console.warn('[Auth0] User has no role assigned:', user?.email);
          } else {
            const cleanRoles = roles.map(r => r.trim());
            const primaryRole = cleanRoles.includes('manager') ? 'manager'
              : cleanRoles.includes('final_approver') ? 'final_approver'
              : cleanRoles.includes('reviewer') ? 'reviewer'
              : cleanRoles.includes('distributor') ? 'distributor'
              : cleanRoles.includes('creator') ? 'creator'
              : cleanRoles.includes('learner') ? 'learner'
              : cleanRoles[0];
            setCurrentRole(primaryRole);
            console.log('[Auth0] role:', primaryRole, 'all roles:', cleanRoles, 'user:', user?.email);
            setRoleReady(true);
            console.log('[Auth0] roleReady set, role:', primaryRole);
          }
        } catch {
          setCurrentRole(null);
        }
      } catch (e) {
        console.error('Failed to get Auth0 token:', e);
        // If Auth0 says login is required (e.g. refresh token expired), redirect cleanly
        // For other errors (network, backend down), don't force logout — just retry on next render
        if (e?.error === 'login_required' || e?.error === 'consent_required') {
          loginWithRedirect({
            authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE }
          });
        }
        // All other errors: leave roleReady=false, user stays on loading screen
        // They can refresh the page manually — don't force a logout loop
      }
    };
    fetchToken();
  }, [isAuthenticated, user, getAccessTokenSilently]);

 

  // Chat floating drag handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!chatDragRef.current.dragging) return;
      setChatPosition({
        x: Math.max(8, chatDragRef.current.offsetX + e.clientX),
        y: Math.max(8, chatDragRef.current.offsetY + e.clientY),
      });
    };
    const handleMouseUp = () => {
      chatDragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Nudge chat icon when minimized
  useEffect(() => {
    chatEffectRunsRef.current += 1;
    if (!chatMountRef.current || chatEffectRunsRef.current <= 2) {
      chatMountRef.current = true;
      return;
    }
    if (chatMinimized) {
      setChatNudge(true);
      setChatTooltip(true);
      const t1 = setTimeout(() => setChatNudge(false), 1800);
      const t2 = setTimeout(() => setChatTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // clear any lingering tooltip when opened
    setChatNudge(false);
    setChatTooltip(false);
  }, [chatMinimized]);

  // Keep ref in sync so create-success handler knows if user switched to another lesson
  useEffect(() => {
    selectedTemplateRef.current = selectedTemplate;
  }, [selectedTemplate]);

  // Nudge gallery icon when entering edit mode (gallery -> canvas)
  useEffect(() => {
    if (prevGalleryModeRef.current === true && galleryViewMode === false) {
      setChatDocked(true);
      setChatCollapsed(false);
      setChatMinimized(true);
      setGalleryNudge(true);
      setGalleryTooltip(true);
      const t1 = setTimeout(() => setGalleryNudge(false), 1800);
      const t2 = setTimeout(() => setGalleryTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevGalleryModeRef.current = galleryViewMode;
  }, [galleryViewMode]);


  // Get state from iframe using postMessage (cross-origin safe)
  const getStateFromIframe = (callback, retries = 3) => {
    const iframe = iframeRef.current;
    if (!iframe) {
      console.warn('Iframe ref is not available');
      callback(null);
      return;
    }

    // Check if iframe is loaded
    try {
      if (!iframe.contentWindow) {
        console.warn('Iframe contentWindow is not available');
        if (retries > 0) {
          // Retry after a short delay
          setTimeout(() => getStateFromIframe(callback, retries - 1), 500);
          return;
        }
        callback(null);
        return;
      }
    } catch (e) {
      console.warn('Cannot access iframe contentWindow (cross-origin):', e);
      // This is expected for cross-origin, continue with postMessage
    }

    let messageReceived = false;
    const handleMessage = (event) => {
      // Accept messages from backend origin
      const isBackendOrigin = event.origin === API_ORIGIN || event.origin === API_ORIGIN_ALT;
      
      if (!isBackendOrigin) {
        return;
      }
      
      if (event.data && event.data.type === 'STATE_DATA') {
        messageReceived = true;
        window.removeEventListener('message', handleMessage);
        callback(event.data.payload);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Request state from iframe
    try {
      iframe.contentWindow.postMessage({ type: 'GET_STATE' }, getIframeOrigin());
    } catch (e) {
      console.error('Error sending postMessage to iframe:', e);
      window.removeEventListener('message', handleMessage);
      if (retries > 0) {
        // Retry after a short delay
        setTimeout(() => getStateFromIframe(callback, retries - 1), 500);
        return;
      }
      callback(null);
      return;
    }
    
    // Timeout after 3 seconds (increased from 1 second)
    setTimeout(() => {
      if (!messageReceived) {
        window.removeEventListener('message', handleMessage);
        if (retries > 0) {
          // Retry
          console.log(`Retrying getStateFromIframe, ${retries} retries left`);
          getStateFromIframe(callback, retries - 1);
        } else {
          console.warn('Timeout waiting for STATE_DATA from iframe');
          callback(null);
        }
      }
    }, 3000);
  };

  const handleSave = () => {
    const iframe = iframeRef.current;
    if (!iframe) {
      setSaveStatus('Error: Iframe not available');
      setTimeout(() => setSaveStatus(''), 2000);
      return;
    }

        // Check if iframe is still loading
        if (isLessonLoading) {
          setSaveStatus('Please wait for lesson to load...');
          setTimeout(() => setSaveStatus(''), 2000);
          return;
        }

    setSaveStatus('Saving...');
    getStateFromIframe((data) => {
      if (data) {
        const payload = { ...data, savedAt: new Date().toISOString() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        setSaveStatus('Error: Could not access iframe. Please try again.');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    });
  };

  const handleClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    // Clear state in iframe via postMessage
    const iframe = iframeRef.current;
    if (iframe) {
      const emptyData = { inputs: [], textareas: [] };
      iframe.contentWindow.postMessage({
        type: 'RESTORE_STATE',
        payload: emptyData
      }, getIframeOrigin());
    }
    setSaveStatus('Cleared');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

    const _pollForDeploy = async (templateId) => {
    const maxPolls = 60; // 3 minutes at 3s intervals
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${API_URL}/board/templates/${encodeURIComponent(templateId)}/deploy-status`,
          withAuthFetchOptions({})
        );
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status === 'success') return { success: true, url: data.deployment_url };
        if (data.status === 'error') return { success: false, error: data.error };
        // status === 'deploying' → keep polling
      } catch (e) { /* network blip, keep trying */ }
    }
    return { success: false, error: 'Deployment timed out. It may have succeeded — check Cloudflare Pages.' };
  };

  const handleDeploy = async () => {
    if (!selectedTemplate) return;
    setIsDeploying(true);
    setDeploymentStatus(null);
    setDeploymentUrl(null);
    try {
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deploy`, withAuthFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }));
      // Read as text first to avoid "body stream already read" if response is non-JSON
      const rawText = await response.text();
      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        const status = response.status;
        if (status === 502 || status === 503 || status === 504) {
          throw new Error('Deployment failed — the server encountered an error. This is usually temporary. Please try again in a moment.');
        }
        throw new Error(`Deployment failed (HTTP ${status}). Please try again.`);
      }
      // Backward compatibility: older backend returned [payload, status_code]
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') data = data[0];

      if (data?.status === 'deploying') {
        // Background deploy started — poll for result
        const result = await _pollForDeploy(selectedTemplate);
        if (result.success) {
          setDeploymentStatus('success');
          setDeploymentUrl(result.url);
          if (result.url) setExistingDeployment({ deployment_url: result.url, deployed_at: new Date().toISOString() });
        } else {
          setDeploymentStatus('error');
          setDeploymentUrl(null);
          alert(`Deployment failed: ${result.error}`);
        }
      } else if (data?.success) {
        // Immediate success (file unchanged, returned cached result)
        setDeploymentStatus('success');
        const url = data.deployment_url || data.production_url;
        setDeploymentUrl(url);
        if (data.deployment) setExistingDeployment(data.deployment);
        else if (url) setExistingDeployment({ deployment_url: url, deployed_at: new Date().toISOString() });
      } else {
        setDeploymentStatus('error');
        setDeploymentUrl(null);
        const rawErr = data?.error || data?.message || `HTTP ${response.status}`;
        let errorMsg = rawErr;
        if (/project limit|reached the limit/i.test(rawErr)) {
          errorMsg = 'Deployment failed: the account has reached the Cloudflare Pages project limit. Please contact your administrator.';
        } else if (/HTTP Error 4\d\d|HTTP Error 5\d\d/i.test(rawErr)) {
          errorMsg = 'Deployment failed due to a server error. Please try again or contact support.';
        }
        alert(`Deployment failed: ${errorMsg}`);
      }
    } catch (error) {
      setDeploymentStatus('error');
      setDeploymentUrl(null);
      alert(`Error deploying lesson: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDeployClick = () => setShowDeployConfirmModal(true);

  const handleDeployConfirm = () => {
    setShowDeployConfirmModal(false);
    setShowDeployModal(true);
    handleDeploy();
  };

  const openInlineAlert = (message) => {
    setInlineAlertMessage(message);
    setShowInlineAlert(true);
  };

  const showToast = (message, type = 'info', duration = 5000) => {
  const id = Date.now() + Math.random();
  setToasts(prev => [...prev, { id, message, type }]);
  setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
};


// Load available templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      
      if (!isAuthenticated) return;
      if (!currentRole) return;
      console.log('[loadTemplates] currentRole:', currentRole);
      try {
        console.log('Loading templates from:', `${API_URL}/board/templates`);
        const response = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Templates response:', data);
        let templates = data.templates || [];

        // Decode role directly from token to avoid stale state
        let tokenRole = null;
        try {
          const tok = withAuthFetchOptions().headers?.Authorization?.split(' ')[1];
          if (tok) {
            const tp = JSON.parse(atob(tok.split('.')[1]));
            const r = (tp['https://lessongen/roles'] || []).map(x => x.trim());
            tokenRole = r.includes('manager') ? 'manager'
              : r.includes('final_approver') ? 'final_approver'
              : r.includes('reviewer') ? 'reviewer'
              : r.includes('distributor') ? 'distributor'
              : r.includes('creator') ? 'creator' : null;
          }
        } catch {}
        console.log('[loadTemplates] tokenRole:', tokenRole);

        if (['creator', 'reviewer', 'final_approver', 'distributor', 'learner'].includes(tokenRole)) {
          const assignedRes = await fetch(`${API_URL}/lessons/assigned`, withAuthFetchOptions());
          const assignedData = await assignedRes.json();
          if (assignedData.success) {
            const assignedIds = assignedData.lessons.map(l => l.template_id);
            if (tokenRole === 'creator') {
              // Creator sees lessons they own OR are assigned to
              const ownedIds = templates.filter(t => t.is_owner).map(t => t.id);
              const allIds = [...new Set([...ownedIds, ...assignedIds])];
              templates = templates.filter(t => allIds.includes(t.id));
            } else {
              // Reviewer/approver/distributor only see assigned lessons
              templates = templates.filter(t => assignedIds.includes(t.id));
            }
          }
        }

        // Restore a "Creating…" card if the user refreshed during generation
        try {
          const savedPending = JSON.parse(localStorage.getItem('lessongen_pending') || 'null');
          if (savedPending?.id) {
            if (templates.find(t => t.id === savedPending.id)) {
              // Generation finished while the browser was away — drop the marker
              localStorage.removeItem('lessongen_pending');
            } else {
              // Still generating — re-add the card so it doesn't disappear
              templates = [...templates, { id: savedPending.id, name: savedPending.name || 'Creating…', is_assessment: savedPending.isAssessment || false }];
              setPendingTemplateId(savedPending.id);
            }
          }
        } catch (_) {}

        setAvailableTemplates(templates);
        syncTemplateOwnershipFromList(templates);
        if (templates.length > 0) {
          setSelectedTemplate(prevTemplate => {
            const currentExists = templates.find(t => t.id === prevTemplate);
            return currentExists ? prevTemplate : templates[0].id;
          });
        } else {
          setSelectedTemplate(null);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };
    loadTemplates();
  }, [API_URL, isAuthenticated, roleReady]);

  useEffect(() => {
    if (!selectedTemplate || !currentToken) return;

    // Fetch review status
    fetch(`${API_URL}/lesson/templates/${selectedTemplate}/review/status`, {
      headers: { Authorization: `Bearer ${currentToken}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) setSelectedLessonStatus(data.review?.status);
      })
      .catch(() => {});

    // Fetch current user's role for this specific lesson
    fetch(`${API_URL}/lesson/templates/${selectedTemplate}/my-role`, {
      headers: { Authorization: `Bearer ${currentToken}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setCurrentLessonRole(data.role);
          console.log('[LessonRole] template:', selectedTemplate, 'role:', data.role);
        }
      })
      .catch(() => {
        // Fallback to global role
        setCurrentLessonRole(currentRole);
      });

  }, [selectedTemplate, currentToken, API_URL]);

  const BYOK_STORAGE_KEYS = { google: 'appstorm_byok_google', openai: 'appstorm_byok_openai', anthropic: 'appstorm_byok_anthropic', deepseek: 'appstorm_byok_deepseek' };
const getStoredApiKeys = () => ({
  google: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.google)) || '',
  openai: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.openai)) || '',
  anthropic: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.anthropic)) || '',
  deepseek: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.deepseek)) || '',
});
const getProvidersSetFromStorage = () => {
  const k = getStoredApiKeys();
  return {
    google: !!(k.google && k.google.trim()),
    openai: !!(k.openai && k.openai.trim()),
    anthropic: !!(k.anthropic && k.anthropic.trim()),
    deepseek: !!(k.deepseek && k.deepseek.trim()),
  };
};

  const fetchKeysConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/board/keys`);
      if (res.ok) {
        const data = await res.json();
        const providers_set = getProvidersSetFromStorage();
        setApiKeysConfig({
          providers_set,
          enabled_models: [],
          use_user_keys_only: !!data.use_user_keys_only,
        });
      }
    } catch (e) {
      console.error('Error fetching keys config:', e);
      setApiKeysConfig(prev => ({ ...prev, providers_set: getProvidersSetFromStorage() }));
    }
  };

  useEffect(() => {
    if (!API_URL || !isAuthenticated) return;
    setApiKeyInputs(getStoredApiKeys());
    fetchKeysConfig();
  }, [API_URL, isAuthenticated]);

  const normalizeModelId = (candidate) => {
    if (!candidate) return '';
    const clean = candidate.trim().toLowerCase();
    if (LEGACY_OPENAI_MODELS.includes(clean)) return OPENAI_MODEL_ID;
    return clean;
  };
  const modelToProvider = {
  gemini: 'google',
  'gemini-3.5-flash': 'google',
  'gpt-5.6-luna': 'openai',
  'gpt-5.6-terra': 'openai',
  [OPENAI_MODEL_ID]: 'openai',
  gpt5: 'openai',
  'claude-sonnet-4-5': 'anthropic',
  'claude-opus-4-6': 'anthropic',
  'deepseek-v4-flash': 'deepseek',
  'deepseek-v4-pro': 'deepseek',
  deepseek: 'deepseek',
};
  const isModelEnabled = (modelId) => {
    const normalized = normalizeModelId(modelId) || modelId;
    if (!apiKeysConfig.use_user_keys_only) return true;
    const provider = modelToProvider[normalized] || modelToProvider[modelId];
    return provider && apiKeysConfig.providers_set?.[provider];
  };
  const enabledModelIds = [
  'gpt-5.6-luna', 'gpt-5.6-terra', 'gemini-3.5-flash', 'gemini', OPENAI_MODEL_ID, 'claude-sonnet-4-5', 'claude-opus-4-6',
  'deepseek-v4-flash', 'deepseek-v4-pro',
    ...(process.env.REACT_APP_SHOW_OLLAMA_MODELS === 'true' ? [
  'ollama-qwen3.5-397b', 'ollama-kimi-k2.5',
  'ollama-qwen3-coder-next', 'ollama-gemma4-31b'
] : [])
  ].filter(isModelEnabled);
  useEffect(() => {
    if (apiKeysConfig.use_user_keys_only && enabledModelIds.length && !enabledModelIds.includes(newTemplateModel)) {
      setNewTemplateModel(enabledModelIds[0]);
    }
  }, [apiKeysConfig.use_user_keys_only, enabledModelIds.join(','), newTemplateModel]);

  const saveApiKeys = () => {
    if (typeof localStorage !== 'undefined') {
      if (apiKeyInputs.google != null) localStorage.setItem(BYOK_STORAGE_KEYS.google, apiKeyInputs.google || '');
      if (apiKeyInputs.openai != null) localStorage.setItem(BYOK_STORAGE_KEYS.openai, apiKeyInputs.openai || '');
      if (apiKeyInputs.deepseek != null) localStorage.setItem(BYOK_STORAGE_KEYS.deepseek, apiKeyInputs.deepseek || '');
    }
    setApiKeysConfig(prev => ({ ...prev, providers_set: getProvidersSetFromStorage() }));
    setShowKeysModal(false);
  };

  // Check ownership when template changes
  useEffect(() => {
    if (selectedTemplate) {
      if (pendingTemplateId && selectedTemplate === pendingTemplateId) {
        return;
      }
      checkTemplateOwnership(selectedTemplate);
    }
  }, [selectedTemplate, pendingTemplateId]);

  // Fetch existing deployment info when template changes
  useEffect(() => {
        const fetchDeploymentInfo = async () => {
      if (!selectedTemplate) return;
      if (pendingTemplateId && selectedTemplate === pendingTemplateId) {
        return;
      }
          try {
            const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deployment`);
        const data = await response.json();
        if (data.success && data.deployment) {
          setExistingDeployment(data.deployment);
        } else {
          setExistingDeployment(null);
        }
      } catch (error) {
        console.error('Error fetching deployment info:', error);
        setExistingDeployment(null);
      } finally {
        setDeploymentStatus(null);
        setDeploymentUrl(null);
        setShowDeployModal(false);
        setShowDeployConfirmModal(false);
      }
    };
    fetchDeploymentInfo();
  }, [selectedTemplate, API_URL, pendingTemplateId]);

  // Resume polling when a pending generation is detected after a browser refresh
  useEffect(() => {
    if (!pendingTemplateId || isActivelyGeneratingRef.current) return;
    let cancelled = false;
    const savedPending = (() => { try { return JSON.parse(localStorage.getItem('lessongen_pending') || 'null'); } catch (_) { return null; } })();
    const pendingName = savedPending?.name || pendingTemplateId;
    const poll = async () => {
      for (let i = 0; i < 400; i++) {
        if (cancelled) return;
        await new Promise(r => setTimeout(r, 3000));
        if (cancelled) return;
        try {
          const sr = await fetch(`${API_URL}/board/templates/${encodeURIComponent(pendingTemplateId)}/create-status`, withAuthFetchOptions({}));
          if (!sr.ok) continue;
          const sd = await sr.json();
          if (sd.status === 'done') {
            const result = sd.result || { success: true, template_id: pendingTemplateId };
            try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
            setPendingTemplateId(null);
            if (result.success) {
              // Refresh gallery list to pick up the real card + thumbnail
              try {
                const tr = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
                const td = await tr.json();
                if (td.templates?.length > 0) { setAvailableTemplates(td.templates); syncTemplateOwnershipFromList(td.templates); }
              } catch (_) {}
              showToast(`✅ "${pendingName}" is ready!`, 'success', 15000);
              setSelectedTemplate(pendingTemplateId);
              if (iframeRef.current) {
                setIsLessonLoading(true);
                setLessonError(null);
                const nonce = Date.now(); setIframeNonce(nonce);
                iframeRef.current.src = buildIframeSrc(pendingTemplateId, nonce);
              }
              try {
                await fetch(`${API_URL}/board/templates/${encodeURIComponent(pendingTemplateId)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
                setQaRunning(true);
                showToast('QA evaluation is running in the background.', 'info', 20000);
                startQaPolling(pendingTemplateId);
              } catch (_) {}
            } else {
              showToast(`❌ Lesson generation failed: ${result.error || 'Unknown error'}`, 'error', 15000);
              setAvailableTemplates(prev => prev.filter(t => t.id !== pendingTemplateId));
            }
            return;
          }
          if (sd.status === 'error') {
            try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
            setPendingTemplateId(null);
            showToast(`❌ Generation failed: ${sd.error || 'Unknown error'}`, 'error', 15000);
            setAvailableTemplates(prev => prev.filter(t => t.id !== pendingTemplateId));
            return;
          }
        } catch (_) { /* network blip — keep polling */ }
      }
      // Timeout
      try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
      setPendingTemplateId(null);
      showToast('⚠️ Generation timed out. Check your gallery.', 'info', 10000);
    };
    poll();
    return () => { cancelled = true; };
  }, [pendingTemplateId, API_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  // Handle iframe load events to show/hide loading indicator and catch errors
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      // If iframe ref is not ready, try again after a short delay
      const retryTimeout = setTimeout(() => {
        if (iframeRef.current) {
          setIsLessonLoading(false);
        }
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }

    const handleLoad = () => {
      console.log('Iframe load event fired, hiding loading indicator');
      // Clear timeout if iframe loads successfully
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      // Hide loading immediately when load event fires
      // postMessage is just a bonus confirmation, but load event is reliable
      setIsLessonLoading(false);
      setLessonError(null); // Clear any previous errors on successful load
      // Inject lightweight nav guard to keep pagination responsive
      try {
        const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
        if (doc) {
          injectNavGuard(doc);
        }
      } catch (err) {
        console.warn('Nav guard injection skipped:', err);
      }
    };

        const handleError = (event) => {
          console.error('Iframe load error:', event);
          setIsLessonLoading(false);
          setLessonError('Failed to load lesson. Please check if the backend server is running.');
        };

    // Set loading initially
    setIsLessonLoading(true);
    setLessonError(null);

    // Listen for load and error events
    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Also check if iframe is already loaded (with retry for cross-origin)
    const checkIfLoaded = () => {
      try {
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          console.log('Iframe already loaded (same-origin)');
          handleLoad();
          return true;
        }
      } catch (e) {
        // Cross-origin, can't access directly - that's expected
        console.log('Cannot access iframe document (cross-origin - expected)');
        // For cross-origin, we rely on postMessage or load event
        // Check if iframe src has loaded by checking if it's not blank
        if (iframe.src && iframe.src !== 'about:blank') {
          // Give it a moment, then check if load event fires
          setTimeout(() => {
            // If still loading after 2 seconds, try to trigger load check
            if (isLessonLoading) {
              console.log('Iframe src set but no load event yet, waiting for postMessage...');
            }
          }, 2000);
        }
      }
      return false;
    };
    
    // Check immediately
    if (!checkIfLoaded()) {
      // Also check after a short delay
      setTimeout(checkIfLoaded, 500);
    }

    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Also check for errors after a timeout
    loadingTimeoutRef.current = setTimeout(() => {
      // If still loading after 15 seconds, show error
      console.log('Loading timeout reached, checking iframe state');
      // Check if iframe actually loaded but message wasn't received
      if (iframeRef.current) {
        try {
          // Try to access iframe to see if it loaded
          const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
          if (iframeDoc && iframeDoc.readyState === 'complete') {
            console.log('Iframe loaded but message not received, hiding loading');
            setIsLessonLoading(false);
            return;
          }
        } catch (e) {
          // Cross-origin, can't access - that's expected
          console.log('Cannot access iframe (cross-origin), checking network');
        }
      }
      
      // Check if backend is reachable
      fetch(`${API_URL}/board/last-modified`)
        .then(() => {
          // Backend is reachable, iframe might just be slow
          setLessonError('Artifacts are taking longer than expected to load. The backend server is running. Please try refreshing or check the browser console for errors.');
        })
        .catch(() => {
          // Backend is not reachable
          setLessonError('Cannot connect to backend server. Please ensure the backend server is running and REACT_APP_API_URL is set.');
        });
      
      setIsLessonLoading(false);
    }, 15000); // Increased to 15 seconds

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [API_URL]);

  // Listen for IFRAME_LOADED message from LessonIframe to hide loading
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received postMessage:', event.data, 'from origin:', event.origin);
      
      // Accept messages from backend origin (iframe) or same origin
      const isBackendOrigin = event.origin === API_ORIGIN || 
                              event.origin === API_ORIGIN_ALT ||
                              event.origin === window.location.origin ||
                              event.origin === '*';
      
      if (!isBackendOrigin && event.origin !== '*') {
        console.log('Ignoring message from origin:', event.origin);
        return;
      }
      
      if (event.data && event.data.type === 'IFRAME_LOADED') {
        console.log('IFRAME_LOADED message received, hiding loading indicator');
        // Clear timeout if message received
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(null); // Clear errors on successful load
      } else if (event.data && event.data.type === 'IFRAME_ERROR') {
        console.log('IFRAME_ERROR message received:', event.data.error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        const details = event.data.details || {};
        const locationBits = [];
        if (details.source) locationBits.push(details.source);
        if (details.line) locationBits.push(`line ${details.line}`);
        if (details.column) locationBits.push(`col ${details.column}`);
        const locationText = locationBits.length ? ` (${locationBits.join(', ')})` : '';
        const stackText = details.stack ? `\n${details.stack}` : '';
        setLessonError((event.data.error || 'Failed to load artifacts content.') + locationText + stackText);
      } else if (event.data && event.data.type === 'STATE_RESTORED') {
        console.log('STATE_RESTORED message received, iframe is loaded');
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(null);
      } else if (event.data && event.data.type === 'PAGE_CHANGE') {
        setCurrentPageId(event.data.pageId);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('Added message listener for IFRAME_LOADED');
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleTemplateChange = (templateId) => {
    if (templateId === 'add-new') {
      setShowAddTemplateModal(true);
      return;
    }
    setSelectedTemplate(templateId);
    const nonce = Date.now();
    setIframeNonce(nonce);
    // Reload iframe with new template
    if (iframeRef.current) {
      setIsLessonLoading(true);
      const newSrc = buildIframeSrc(templateId, nonce);
      iframeRef.current.src = newSrc;
    }
  };

      const handleDeleteClick = (templateId) => {

    const template = availableTemplates.find(t => t.id === templateId);
    setTemplateToDelete({ id: templateId, name: template ? template.name : templateId });
    setDeleteStep(1);
    setShowDeleteModal(true);
  };

  const handleCopyClick = (templateId) => {
    const template = availableTemplates.find(t => t.id === templateId);
    const baseName = template ? template.name : templateId;
    setCopyLessonName(`${baseName} Copy`);
    setShowCopyModal(true);
  };

      const handleCopyLesson = async () => {
        if (!copyLessonName.trim()) {
          alert('Please enter a name for the copied lesson');
          return;
        }

        setIsCopying(true);
            try {
                  const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/copy`, withAuthFetchOptions({
                method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              new_name: copyLessonName.trim()
            }),
          }));

          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || data.detail || 'Copy failed');
          }

              const templatesResponse = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
          const templatesData = await templatesResponse.json();
          if (templatesData.templates && templatesData.templates.length > 0) {
            setAvailableTemplates(templatesData.templates);
          syncTemplateOwnershipFromList(templatesData.templates);
          }

      setSelectedTemplate(data.template_id);
      setShowCopyModal(false);
      setCopyLessonName('');
      clearTemplateChatHistory(data.template_id);
      checkTemplateOwnership(data.template_id);

      if (iframeRef.current) {
        setIsLessonLoading(true);
        const nonce = Date.now();
        setIframeNonce(nonce);
        iframeRef.current.src = buildIframeSrc(data.template_id, nonce);
      }
        } catch (error) {
          console.error('Error copying lesson:', error);
          alert('Failed to copy lesson. Please try again.');
        } finally {
          setIsCopying(false);
        }
      };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    if (deleteStep === 1) {
      // First confirmation - move to second step
      setDeleteStep(2);
      return;
    }

    // Second confirmation - proceed with deletion
    setIsDeletingTemplate(true);
    try {
          const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateToDelete.id)}`, withAuthFetchOptions({
            method: 'DELETE',
          }));

      const data = await response.json();

          if (data.success) {
        // Clear chat history for the deleted template BEFORE reloading templates
        console.log(`[App] Deleting lesson ${templateToDelete.id}, clearing chat history...`);
        clearTemplateChatHistory(templateToDelete.id);

        // Reload templates list
            const templatesResponse = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
            const templatesData = await templatesResponse.json();
            if (templatesData.templates && templatesData.templates.length > 0) {
              setAvailableTemplates(templatesData.templates);
            syncTemplateOwnershipFromList(templatesData.templates);
          if (selectedTemplate === templateToDelete.id) {
            const next = templatesData.templates[0].id;
            setSelectedTemplate(next);
            if (iframeRef.current) {
              setIsLessonLoading(true);
              const nonce = Date.now();
              setIframeNonce(nonce);
              iframeRef.current.src = buildIframeSrc(next, nonce);
            }
          }
        } else {
          setAvailableTemplates([]);
          if (selectedTemplate === templateToDelete.id) {
            setSelectedTemplate(null);
          }
        }
        // Close modal, reset, and go to gallery
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        setDeleteStep(1);
        setGalleryViewMode(true);
          } else {
            alert(`Error deleting lesson: ${data.error || data.detail || 'Unknown error'}`);
          }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting lesson: ${error.message}`);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteStep(1);
  };

  const handleOpenTemplateFromGallery = (templateId) => {
    if (pendingTemplateId && templateId === pendingTemplateId) {
      return; // card is still generating — do not navigate away from gallery
    }
    setGalleryViewMode(false); // enter slide edit view
    setLessonError(null);
    setIsLessonLoading(true);
    setSelectedTemplate(templateId);
    if (iframeRef.current) {
      const nonce = Date.now();
      setIframeNonce(nonce);
      iframeRef.current.src = buildIframeSrc(templateId, nonce);
    }
  };

  const MAX_TEMPLATE_IMAGES = 6;
  const readTemplateFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  const handleTemplateImageSelect = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    const valid = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`Image ${file.name} must be less than 10MB`);
        continue;
      }
      valid.push(file);
    }
    const toAdd = valid.slice(0, MAX_TEMPLATE_IMAGES - newTemplateImages.length);
    if (toAdd.length === 0) {
      alert(`You can add up to ${MAX_TEMPLATE_IMAGES} reference images.`);
      if (templateImageInputRef.current) templateImageInputRef.current.value = '';
      return;
    }
    const newFiles = [...newTemplateImages, ...toAdd].slice(0, MAX_TEMPLATE_IMAGES);
    setNewTemplateImages(newFiles);
    try {
      const newPreviews = await Promise.all(toAdd.map(readTemplateFileAsDataURL));
      setNewTemplateImagePreviews((prev) => [...prev, ...newPreviews].slice(0, MAX_TEMPLATE_IMAGES));
    } catch (err) {
      console.error('Error reading template images:', err);
      setNewTemplateImages(newTemplateImages);
    }
    if (templateImageInputRef.current) templateImageInputRef.current.value = '';
  };

  const removeTemplateImage = (index) => {
    if (index !== undefined) {
      setNewTemplateImages((prev) => prev.filter((_, i) => i !== index));
      setNewTemplateImagePreviews((prev) => prev.filter((_, i) => i !== index));
    } else {
      setNewTemplateImages([]);
      setNewTemplateImagePreviews([]);
    }
    if (templateImageInputRef.current) templateImageInputRef.current.value = '';
  };

  const handleTemplateFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx', 'xls', 'pdf', 'docx', 'pptx'].includes(fileExt)) {
        alert('Please select a supported file (.csv, .xlsx, .xls, .pdf, .docx, .pptx)');
        return;
      }
      
      // Validate file size (max 50MB for data files)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      
      setNewTemplateFile(file);
      setIsUploadingFile(true);

      // Upload the file immediately
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();
        if (data.success) {
          setNewTemplateFilePath(data.file_path);
          console.log(`Template file uploaded: ${data.file_path}`);
        } else {
          alert(`Upload failed: ${data.error || 'Unknown error'}`);
          setNewTemplateFile(null);
        }
      } catch (error) {
        console.error('Error uploading template file:', error);
        alert('Failed to upload file. Please try again.');
        setNewTemplateFile(null);
      } finally {
        setIsUploadingFile(false);
      }
    }
  };

  const removeTemplateFile = () => {
    setNewTemplateFile(null);
    setNewTemplateFilePath(null);
    if (templateFileInputRef.current) {
      templateFileInputRef.current.value = '';
    }
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix (data:image/png;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const deriveLessonName = (description) => {
    const cleaned = (description || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return 'New Lesson';
    const firstSentence = cleaned.split(/[.!?]/)[0] || cleaned;
    const words = firstSentence.split(' ').filter(Boolean).slice(0, 6);
    return words.join(' ') || 'New Lesson';
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateIsAssessment && !newTemplateDescription.trim()) {
      alert('Please provide a lesson description');
      return;
    }
    if (newTemplateIsAssessment && newTemplateAssessmentSource === 'topic' && !newTemplateAssessmentTopic.trim()) {
      alert('Please enter a topic for the assessment');
      return;
    }
    if (newTemplateIsAssessment && newTemplateAssessmentSource === 'template_ids' && !newTemplateAssessmentTemplateIds.trim()) {
      alert('Please select at least one lesson to base the assessment on');
      return;
    }
    if (createInFlightRef.current) {
      return; // already creating (e.g. double-click) -- only one create at a time
    }
    createInFlightRef.current = true;
setIsCreatingTemplate(true);

// Save form data before clearing
const previousTemplateId = selectedTemplate;
// ── Save assessment fields before modal clears ──
const savedIsAssessment = newTemplateIsAssessment;
const savedAssessmentSource = newTemplateAssessmentSource;
const savedAssessmentTopic = newTemplateAssessmentTopic;
const savedAssessmentTemplateIds = newTemplateAssessmentTemplateIds
  .split(',').map(s => s.trim()).filter(Boolean);
const savedQuestionCount = newTemplateQuestionCount ? parseInt(newTemplateQuestionCount) : null;
const savedName = newTemplateName.trim() ||
  (savedIsAssessment
    ? (savedAssessmentTopic.trim() || 'Assessment')
    : deriveLessonName(newTemplateDescription));
const savedDescription = newTemplateDescription.trim();
const savedFilePath = newTemplateFilePath;
const imagesToSend = [...newTemplateImages];
    setCreatingTemplateName(savedName);
    setCreatingTemplateDescription(savedDescription);
    // Save assessment fields before modal clears


    // Optimistically add the template to the list (use unique id so only this card shows "Creating...")
    const tempTemplateId = `creating-${Date.now()}`;
    const tempTemplate = { id: tempTemplateId, name: savedName, is_assessment: savedIsAssessment };
    setAvailableTemplates(prev => [...prev, tempTemplate]);
    setSelectedTemplate(tempTemplateId);
    setPendingTemplateId(tempTemplateId);
    setIsLessonLoading(true);
    setLessonError(null);

    // Close modal immediately for better UX
    setShowAddTemplateModal(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateModel('gpt-5.6-luna'); // Reset to default model
    setNewTemplateIsAssessment(false);
    setNewTemplateAssessmentSource('topic');
    setNewTemplateAssessmentTopic('');
    setNewTemplateAssessmentTemplateIds('');
    setNewTemplateQuestionCount(''); 
    
    // Prepare image data (multiple)
    const imagesPayload = [];
    for (let i = 0; i < imagesToSend.length; i++) {
      const item = imagesToSend[i];
      if (item instanceof Blob || item instanceof File) {
        const b64 = await convertImageToBase64(item);
        imagesPayload.push({ base64: b64, mime_type: item.type || 'image/png' });
      } else if (typeof item === 'string' && item.includes('base64,')) {
        // Already a data URL from backlog preview
        imagesPayload.push({ base64: item.split('base64,')[1], mime_type: 'image/png' });
      } else if (typeof item === 'string') {
        // Plain base64 string
        imagesPayload.push({ base64: item, mime_type: 'image/png' });
      }
      // Skip plain objects like { name: 'ref_image_0' } with no actual image data
    }
    
    // Clear image and file state
    removeTemplateImage();
    removeTemplateFile();
    
    try {
          const requestBody = {
            name: savedName,
            description: savedDescription,
            model: newTemplateModel
          };
      const storedKeys = getStoredApiKeys();
      if (storedKeys.google?.trim()) requestBody.google_key = storedKeys.google.trim();
      if (storedKeys.openai?.trim()) requestBody.openai_key = storedKeys.openai.trim();
      if (storedKeys.deepseek?.trim()) requestBody.deepseek_key = storedKeys.deepseek.trim();

      if (imagesPayload.length === 1) {
        requestBody.image_base64 = imagesPayload[0].base64;
        requestBody.image_mime_type = imagesPayload[0].mime_type;
      } else if (imagesPayload.length > 1) {
        requestBody.images = imagesPayload;
      }
      
      if (savedFilePath) {
        requestBody.uploaded_file_path = savedFilePath;
      }
      
          let data = {};
      if (savedIsAssessment) {
        const assessBody = {
  template_id: tempTemplateId,
  source: savedAssessmentSource,
  topic: savedAssessmentTopic || savedName,
  template_ids: savedAssessmentTemplateIds,
  question_count: savedQuestionCount ? Number(savedQuestionCount) : undefined,
  model: newTemplateModel,
};
        const storedKeys2 = getStoredApiKeys();
        if (storedKeys2.google?.trim()) assessBody.google_key = storedKeys2.google.trim();
        if (storedKeys2.openai?.trim()) assessBody.openai_key = storedKeys2.openai.trim();
        if (storedKeys2.deepseek?.trim()) assessBody.deepseek_key = storedKeys2.deepseek.trim();

        const assessResponse = await fetch(`${API_URL}/lesson/generate-assessment`, withAuthFetchOptions({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assessBody),
        }));

        const assessRawText = await assessResponse.text();
        const parseJsonSafely2 = (text) => {
          if (!text) return {};
          try { return JSON.parse(text); }
          catch {
            const match = text.match(/\{[\s\S]*\}/);
            if (match) { try { return JSON.parse(match[0]); } catch { return {}; } }
            return {};
          }
        };
        data = parseJsonSafely2(assessRawText);
        if (!assessResponse.ok || data.blocked) {
          const messageFromData = data.block_reason || data.error || (Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(", ") : typeof data.detail === "object" && data.detail !== null ? JSON.stringify(data.detail) : data.detail) || data.reason || data.message || '';
          const messageFromText = assessRawText && !/^\s*[\{\[]/.test(assessRawText) ? assessRawText : '';
          const resolvedMessage = messageFromData || messageFromText || 'Assessment generation failed.';
          setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
          try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
          setPendingTemplateId(null);
          setSelectedTemplate(previousTemplateId);
          if (iframeRef.current) {
            setIsLessonLoading(true);
            const nonce = Date.now();
            setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
          }
          setIsLessonLoading(false);
          setCreatingTemplateName('');
          setCreatingTemplateDescription('');
          openInlineAlert(resolvedMessage);
          setIsCreatingTemplate(false);
          return;
        }
        // Falls through to the polling block below
      // Falls through to the polling block below
      } else {
      // ── Normal lesson path ────────────────────────────────────────────────
      const response = await fetch(`${API_URL}/board/templates/create`, withAuthFetchOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }));
      const rawText = await response.text();
      data = {};
      const parseJsonSafely = (text) => {
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch (error) {
          const match = text.match(/\{[\s\S]*\}/);
          if (match) {
            try {
              return JSON.parse(match[0]);
            } catch (innerError) {
              return {};
            }
          }
          return {};
        }
      };
      data = parseJsonSafely(rawText);
          const messageFromData = data.block_reason || data.error || (Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(", ") : typeof data.detail === "object" && data.detail !== null ? JSON.stringify(data.detail) : data.detail) || data.reason || data.message || '';
      const messageFromText = rawText && !/^\s*[\{\[]/.test(rawText) ? rawText : '';
      const resolvedMessage = messageFromData || messageFromText || 'Request failed. Please try again.';
      
      if (!response.ok) {
        const msg = messageFromData || messageFromText || `HTTP ${response.status}: ${response.statusText}`;
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
        setPendingTemplateId(null);
          setSelectedTemplate(previousTemplateId);
          if (iframeRef.current) {
            setIsLessonLoading(true);
            const nonce = Date.now();
            setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
          }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(msg);
        setIsCreatingTemplate(false);
        return;
      }
      
      if (data.blocked) {
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
        setPendingTemplateId(null);
        setSelectedTemplate(previousTemplateId);
        if (iframeRef.current) {
          setIsLessonLoading(true);
          const nonce = Date.now();
          setIframeNonce(nonce);
          iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
        }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(resolvedMessage);
        setIsCreatingTemplate(false);
        return;
      }
      } // close else (normal lesson path)

      // ── Background generation: backend returned 202 with a real template ID ─
      if (data.status === 'creating' && data.template_id) {
        const realId = data.template_id;

        // Swap the optimistic placeholder for the real ID so the gallery card
        // and ChatAdvisor both update immediately.
        setAvailableTemplates(prev =>
          prev.map(t => t.id === tempTemplateId ? { ...t, id: realId } : t)
        );
        setSelectedTemplate(realId);
        selectedTemplateRef.current = realId;
        setPendingTemplateId(realId);
        // Persist so the "Creating…" card survives a browser refresh
        try { localStorage.setItem('lessongen_pending', JSON.stringify({ id: realId, name: savedName, isAssessment: savedIsAssessment })); } catch (_) {}

        // Poll /create-status every 3 s for up to 20 minutes
        isActivelyGeneratingRef.current = true;
        const maxPolls = 400;
        let finalResult = null;
        for (let i = 0; i < maxPolls; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const sr = await fetch(
              `${API_URL}/board/templates/${encodeURIComponent(realId)}/create-status`,
              withAuthFetchOptions({})
            );
            if (!sr.ok) continue;
            const sd = await sr.json();
            if (sd.status === 'done') { finalResult = sd.result || { success: true, template_id: realId }; break; }
            if (sd.status === 'error') { finalResult = { success: false, error: sd.error || 'Generation failed' }; break; }
          } catch (_) { /* network blip — keep polling */ }
        }
        if (!finalResult) finalResult = { success: false, error: 'Generation timed out. It may still complete — check your gallery.' };
        isActivelyGeneratingRef.current = false;
        data = finalResult;
      }

      // ── Handle final result (same path whether sync or async) ───────────────
      if (data.success) {
        const finalId = data.template_id || selectedTemplateRef.current;
        await new Promise(r => setTimeout(r, 300));
        try {
          const tr = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
          const td = await tr.json();
          if (td.templates?.length > 0) {
            setAvailableTemplates(td.templates);
            syncTemplateOwnershipFromList(td.templates);
          }
        } catch (_) {}
        localStorage.setItem(`chat_model_${finalId}`, newTemplateModel);
        try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
        setPendingTemplateId(null);
        // Link task regardless of which template is currently selected in the editor
        if (window.__pendingBacklogId) {
          const pendingId = window.__pendingBacklogId;
          window.__pendingBacklogId = null;
          fetch(`${API_URL}/my/tasks/${pendingId}/link-lesson`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
            body: JSON.stringify({ lesson_id: finalId }),
          }).then(() => {
            // Tell MyTasks to refresh its list so the task moves to Completed
            window.dispatchEvent(new CustomEvent('myTasksRefresh'));
          }).catch(() => {});
        }

        if (selectedTemplateRef.current === finalId || selectedTemplateRef.current === tempTemplateId) {
          setSelectedTemplate(finalId);
          // Reload chat history — backend seeds it during creation but the panel loaded too early (before creation finished)
          setTimeout(() => window.dispatchEvent(new CustomEvent('chatHistoryRefresh', { detail: { template: finalId } })), 800);
          if (iframeRef.current) {
            setIsLessonLoading(true);
            setLessonError(null);
            const nonce = Date.now(); setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(finalId, nonce);
          }
          // Kick off QA in background — lesson is already visible
          try {
            await fetch(`${API_URL}/board/templates/${encodeURIComponent(finalId)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
            console.log('[QA] polling started for', finalId);
            setQaRunning(true);
            showToast('Lesson is ready! QA evaluation is running in the background.', 'info', 30000);
            startQaPolling(finalId);
          } catch (_) {}
        } else {
          openInlineAlert(`"${savedName}" is ready. Open it from the gallery.`);
        }
        try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
        setPendingTemplateId(null);
      } else {
        const failId = data.template_id || selectedTemplateRef.current || tempTemplateId;
        setAvailableTemplates(prev => prev.filter(t => t.id !== failId && t.id !== tempTemplateId));
        try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
        setPendingTemplateId(null);
        setSelectedTemplate(previousTemplateId);
        if (iframeRef.current) {
          setIsLessonLoading(true);
          const nonce = Date.now(); setIframeNonce(nonce);
          iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
        }
        setIsLessonLoading(false);
        openInlineAlert(`Error creating lesson: ${data.error || 'Generation failed.'}`);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
      try { localStorage.removeItem('lessongen_pending'); } catch (_) {}
      setPendingTemplateId(null);
      setSelectedTemplate(previousTemplateId);
      if (iframeRef.current) {
        setIsLessonLoading(true);
        const nonce = Date.now(); setIframeNonce(nonce);
        iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
      }
      setIsLessonLoading(false);
      setCreatingTemplateName('');
      setCreatingTemplateDescription('');
      openInlineAlert(`Error creating lesson: ${error.message}`);
    } finally {
      createInFlightRef.current = false;
      setIsCreatingTemplate(false);
      setCreatingTemplateName('');
      setCreatingTemplateDescription('');
    }
  };

  const handleStartupSignIn = () => loginWithRedirect({
  authorizationParams: { prompt: 'login' }
});

  if (authChecking) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Checking session...</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>Please wait</div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && !roleReady) {
    // If currentRole is explicitly null (token fetched but no role assigned), show the contact-admin screen
    if (currentRole === null) {
      return (
        <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
          <div style={{ width: 'min(440px, calc(100vw - 24px))', padding: '20px', borderRadius: '14px', background: '#fff', color: '#0f172a', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <h2 style={{ margin: '0 0 8px 0' }}>No role assigned</h2>
            <p style={{ color: '#475569', margin: '0 0 14px 0' }}>
              Your account hasn't been assigned a role yet. Please contact your administrator to get access.
              Once assigned, click <strong>Refresh</strong> to continue.
            </p>
            <button
              onClick={async () => {
                // Force a fresh token (bypasses cache) so new role is picked up without clearing cache
                try {
                  const token = await getAccessTokenSilently({
                    cacheMode: 'off',
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE }
                  });
                  setCurrentToken(token);
                  const tp = JSON.parse(atob(token.split('.')[1]));
                  const roles = (tp['https://lessongen/roles'] || []).map(r => r.trim());
                  if (roles.length > 0) {
                    const primaryRole = roles.includes('manager') ? 'manager'
                      : roles.includes('final_approver') ? 'final_approver'
                      : roles.includes('reviewer') ? 'reviewer'
                      : roles.includes('distributor') ? 'distributor'
                      : roles.includes('creator') ? 'creator'
                      : roles.includes('learner') ? 'learner'
                      : roles[0];
                    setCurrentRole(primaryRole);
                    setRoleReady(true);
                  }
                } catch (e) {
                  console.error('Refresh failed:', e);
                }
              }}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', background: '#1d4ed8', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', marginBottom: '8px' }}
            >
              Refresh
            </button>
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
            >
              Sign out
            </button>
          </div>
        </div>
      );
    }

    // Still fetching token — show spinner with a timeout fallback
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Loading your profile...</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>Please wait</div>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ width: 'min(440px, calc(100vw - 24px))', padding: '20px', borderRadius: '14px', background: '#ffffff', color: '#0f172a', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
          <h2 style={{ margin: '0 0 8px 0' }}>Sign in required</h2>
          <p style={{ margin: '0 0 14px 0', color: '#475569' }}>
            Sign in first, then the gallery and lessons will load.
          </p>
          <button
            onClick={handleStartupSignIn}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            Sign in with Auth0
          </button>
        </div>
      </div>
    );
  }

  if (isAuthenticated && currentRole === 'learner') {
    return <LearnerView token={currentToken} />;
  }

  

  if (isAuthenticated && !authChecking && roleReady && !currentRole) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ width: 'min(440px, calc(100vw - 24px))', padding: '20px', borderRadius: '14px', background: '#fff', color: '#0f172a', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
          <h2 style={{ margin: '0 0 8px 0' }}>No role assigned</h2>
          <p style={{ color: '#475569', margin: '0 0 14px 0' }}>Your account has no role assigned. Please contact your manager.</p>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin} })}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {showAdmin && (
        <div className="admin-modal-backdrop" onClick={() => setShowAdmin(false)}>
          <div className="admin-modal-panel" onClick={e => e.stopPropagation()}>
            <AdminPage token={currentToken} onExit={() => setShowAdmin(false)} />
          </div>
        </div>
      )}
      {showAdmin && (
        <div className="admin-modal-backdrop" onClick={() => setShowAdmin(false)}>
          <div className="admin-modal-panel" onClick={e => e.stopPropagation()}>
            <AdminPage token={currentToken} onExit={() => setShowAdmin(false)} />
          </div>
        </div>
      )}
          <div className={`layout-shell ${chatDocked && !galleryViewMode ? 'chat-docked' : ''} ${chatDocked && !galleryViewMode && chatCollapsed ? 'chat-collapsed' : ''}`}>
      <div className={`left-rail ${galleryViewMode ? 'gallery-mode' : ''}`}>
            <aside className="side-rail">
          <div className="brand-mark mini">
            <img
              src={`${process.env.PUBLIC_URL}/logo/zero2AI_logomain.svg`}
              alt="Zero2AI Logo"
              className="brand-logo mini"
            />
          </div>
          <div className="rail-actions">
            {currentRole === 'manager' && (
            <button className="rail-btn icon rail-new" onClick={() => setShowAddTemplateModal(true)} title="New Lesson">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 5v14M5 12h14"></path>
              </svg>
              <span className="sr-only">New Lesson</span>
            </button>
            )}
            <button 
              className={`rail-btn icon rail-gallery ${galleryNudge ? 'nudge' : ''}`} 
              onClick={() => setGalleryViewMode(true)} 
              title="Gallery"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                <rect x="3" y="14" width="7" height="7" rx="1"></rect>
                <rect x="14" y="14" width="7" height="7" rx="1"></rect>
              </svg>
              <span className="sr-only">Gallery</span>
              {galleryTooltip && <span className="rail-tooltip">Go to Gallery</span>}
            </button>
            {['manager', 'creator'].includes(currentRole) && <button 
              className={`rail-btn icon rail-chat ${chatNudge ? 'nudge' : ''}`}
                  onClick={() => {
                    setChatDocked(true);
                    setChatCollapsed(false);
                    setChatMinimized(true);
                  }} 
                  title="Lesson Copilot"
                >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 11.5a8.38 8.38 0 0 1-1.9 5.4 8.5 8.5 0 0 1-6.6 3.1 8.38 8.38 0 0 1-5.4-1.9L3 20l1.9-4.1a8.38 8.38 0 0 1-1.9-5.4 8.5 8.5 0 0 1 3.1-6.6A8.38 8.38 0 0 1 11.5 2H12a8.5 8.5 0 0 1 8 9.5Z"></path>
              </svg>
              <span className="sr-only">Open Lesson Copilot</span>
              {chatTooltip && <span className="rail-tooltip">Lesson Copilot</span>}
            </button>}
            <button
              className="rail-btn icon rail-keys"
              onClick={() => setShowKeysModal(true)}
              title="API Keys (Google, OpenAI, Anthropic, DeepSeek)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="7" cy="12" r="3"></circle>
                <path d="M10 12h11"></path>
                <path d="M18 12v3"></path>
                <path d="M15 12v2"></path>
              </svg>
             
              <span className="sr-only">API Keys</span>
              <span className="rail-tooltip auto-hide">API Keys</span>
            </button>
            {currentRole === 'manager' && (
              <button
                className="rail-btn icon rail-admin"
                onClick={() => setShowAdmin(true)}
                title="Admin Panel"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                <span className="sr-only">Admin Panel</span>
                <span className="rail-tooltip auto-hide">Admin Panel</span>
              </button>
            )}
            {currentRole === 'manager' && (
              <button
                className="rail-btn icon"
                onClick={() => window.open('/monitor-cost', '_blank')}
                title="Cost Monitor"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <span className="sr-only">Cost Monitor</span>
                <span className="rail-tooltip auto-hide">Cost Monitor</span>
              </button>
            )}
            <button
              className="rail-btn icon"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin} })}
              title={`Sign out (${user?.email})`}
              style={{ marginTop: 'auto' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span className="sr-only">Sign out</span>
              <span className="rail-tooltip auto-hide">Sign out ({user?.email})</span>
            </button>
          </div>
        </aside>
            {chatDocked && !galleryViewMode && (
              <aside className={`chat-sidebar ${chatCollapsed ? 'collapsed' : ''}`}>
                <div className="chat-sidebar-header">
                  {!chatCollapsed && <span className="chat-sidebar-title">Lesson Copilot</span>}
                  <div className="chat-sidebar-actions">
                <button
                  className="icon-btn small"
                  onClick={() => setChatCollapsed(!chatCollapsed)}
                  title={chatCollapsed ? 'Expand chat' : 'Collapse chat'}
                >
                  {chatCollapsed ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  )}
                  <span className="sr-only">{chatCollapsed ? 'Expand chat' : 'Collapse chat'}</span>
                </button>
                <button
                  className="icon-btn small"
                  onClick={() => {
                    setChatDocked(false);
                    setChatMinimized(false);
                  }}
                  title="Pop out chat"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 3h3v3"></path>
                    <path d="M21 3l-7 7"></path>
                    <rect x="3" y="7" width="14" height="14" rx="2" ry="2"></rect>
                  </svg>
                  <span className="sr-only">Pop out chat</span>
                </button>
              </div>
            </div>
            {!chatCollapsed && (
              <div className="chat-sidebar-body">
                {selectedTemplate ? (
                      <ChatAdvisor
                        key={`chat-${selectedTemplate}`}
                        selectedTemplate={selectedTemplate}
                        isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
                        isCreatingTemplate={isCreatingTemplate}
                        creatingTemplateDescription={creatingTemplateDescription}
                        pendingTemplateId={pendingTemplateId}
                        apiKeysConfig={apiKeysConfig}
                        defaultModelForTemplate={availableTemplates.find(t => t.id === selectedTemplate)?.default_model}
                      isEditMode={isEditMode}
                      setIsEditMode={setIsEditMode}
                      getAccessTokenSilently={getAccessTokenSilently}
                      />
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>It's empty</p>
                    <p>Select or create a lesson to chat with the copilot.</p>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
              <div className="main-pane">
          <div className="top-actions compact">
            <img
              src={`${process.env.PUBLIC_URL}/logo/Arbiteria_logo_new.png`}
              alt="Arbiteria logo"
              className="top-right-logo-abs"
            />
            <div className="top-brand-center">
              <div className="brand-mark mini center">
                <div className="brand-circles mini">
                  <span></span><span></span><span></span>
                </div>
                <div className="brand-type">
                  <div className="brand-kicker">
                    <span className="brand-lesson">Lesson</span>
                    <span className="brand-gen">Gen</span>
                  </div>
                  <div className="brand-tagline">Interactive mini-lessons</div>
                </div>
              </div>
            </div>
            <div className="top-actions-mid">
</div>

            <div className="top-actions-right">
              <span className="save-status">{saveStatus}</span>
              
              <button className="icon-btn" onClick={handleSave} title="Save inputs">
                <span aria-hidden>💾</span>
                <span className="sr-only">Save</span>
              </button>
              <button className="icon-btn" onClick={handleClear} title="Clear saved state">
                <span aria-hidden>🧹</span>
                <span className="sr-only">Clear</span>
              </button>
            </div>
          </div>
      <div className={`main-layout ${isFullscreen ? 'fullscreen-mode' : ''} ${galleryViewMode ? 'gallery-view-mode' : ''}`}>
        <div className={`lesson-column canvas ${isFullscreen ? 'fullscreen' : ''} ${galleryViewMode ? 'gallery-view' : ''}`}>
          <div className="inline-gallery" style={{ display: galleryViewMode ? 'block' : 'none' }} aria-hidden={!galleryViewMode}>
              {availableTemplates.length === 0 ? (
                <div className="gallery-empty-state" style={{ textAlign: 'center', padding: '3rem', color: '#facc15' }}>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>It's empty here</p>
                  <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No lessons yet.</p>
                  <p style={{ marginBottom: '1rem' }}>Create your first lesson to get started.</p>
                  {currentRole === 'manager' && (
                  <button
                    className="modal-button primary"
                    onClick={() => setShowAddTemplateModal(true)}
                  >
                    Create lesson
                  </button>
                  )}
                </div>
              ) : (
              <div className="gallery-grid inline">
                {availableTemplates.map((template) => {
                  const isPendingPreview = pendingTemplateId && template.id === pendingTemplateId;
                  return (
                  <div
                    key={template.id}
                    className="gallery-item inline"
                    onClick={() => handleOpenTemplateFromGallery(template.id)}
                    style={isPendingPreview ? { cursor: 'not-allowed' } : undefined}
                    title={isPendingPreview ? 'Lesson is still generating, please wait…' : `Open ${template.name}`}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      const centerX = rect.width / 2;
                      const centerY = rect.height / 2;
                      const rotateX = ((y - centerY) / centerY) * -5;
                      const rotateY = ((x - centerX) / centerX) * 5;
                      e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
                      e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
                      e.currentTarget.style.setProperty('--rotate-x', `${rotateX}deg`);
                      e.currentTarget.style.setProperty('--rotate-y', `${rotateY}deg`);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.setProperty('--mouse-x', `50%`);
                      e.currentTarget.style.setProperty('--mouse-y', `50%`);
                      e.currentTarget.style.setProperty('--rotate-x', `0deg`);
                      e.currentTarget.style.setProperty('--rotate-y', `0deg`);
                    }}
                  >
                    <div className="gallery-card">
                      <div className="gallery-face front">
                        <div className="gallery-hover-anchor">
                          <div className="gallery-hover-bubble">
                            <p className="bubble-title">{template.name}</p>
                            <p className="bubble-text">
                              {template.description && template.description.trim().length > 0
                                ? template.description
                                : 'Interactive lesson with multimedia screens, checks, and a scored assessment.'}
                            </p>
                            <p className="bubble-text subtle">
                              {template.estimated_minutes
                                ? `${template.estimated_minutes} min · ARCS hooks + assessment`
                                : 'ARCS hooks + assessment (80% to certify)'}
                            </p>
                            <span className="bubble-cta">Click to open & edit</span>
                          </div>
                        </div>
                        <div className="gallery-item-preview">
                          {isPendingPreview ? (
                            <div className="gallery-preview-pending">
                              <div className="gallery-loading-spinner"></div>
                              <span>{newTemplateIsAssessment ? 'Building Assessment…' : 'Creating…'}</span>
                            </div>
                          ) : (
                            <div className="gallery-preview-static">
                              {/* Fallback text always rendered; image layers on top and hides on error */}
                              <div className="gallery-static-badge" style={template.is_assessment ? { background: '#6366f1', color: '#fff' } : {}}>
                                {template.is_assessment ? '📝 Assessment' : 'Lesson'}
                              </div>
                              <div className="gallery-static-title">{template.name}</div>
                              <div className="gallery-static-subtitle">
                                {template.estimated_minutes
                                  ? `${template.estimated_minutes} min`
                                  : 'Open to view and edit'}
                              </div>
                              {template.thumbnail_url && (
                                <>
                                  <img
                                    src={`${API_URL}${template.thumbnail_url}`}
                                    alt=""
                                    className="gallery-static-image"
                                    loading="lazy"
                                    style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',borderRadius:10}}
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                  <div style={{
                                    position:'absolute',top:0,left:0,right:0,zIndex:3,
                                    background:'linear-gradient(rgba(0,0,0,0.62),transparent)',
                                    padding:'9px 10px 22px',
                                    color:'#fff',
                                    fontSize:'12px',
                                    fontWeight:600,
                                    borderRadius:'10px 10px 0 0',
                                    lineHeight:1.3,
                                    pointerEvents:'none',
                                    overflow:'hidden',
                                    whiteSpace:'nowrap',
                                    textOverflow:'ellipsis',
                                  }}>{template.name}</div>
                                </>
                              )}
                              {/* Assessment corner badge — sits above thumbnail via z-index */}
                              {template.is_assessment && (
                                <span style={{
                                  position: 'absolute', top: '8px', right: '8px', zIndex: 4,
                                  padding: '3px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 700,
                                  background: '#6366f1', color: '#fff',
                                  boxShadow: '0 2px 6px rgba(99,102,241,0.5)',
                                  letterSpacing: '0.02em', lineHeight: 1.4,
                                }}>Assessment</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="gallery-item-info">
                          <h3 className="gallery-item-name">{template.name}</h3>
                          {template.is_assessment && (
                            <span style={{
                              display: 'inline-block', marginTop: '2px',
                              padding: '1px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                              background: 'rgba(99,102,241,0.12)', color: '#6366f1',
                            }}>📝 Assessment</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );})}
              </div>
              )}
            </div>
          <div className="lesson-frame" ref={lessonFrameRef} style={{ display: galleryViewMode ? 'none' : 'block' }} aria-hidden={galleryViewMode}>
            {!selectedTemplate ? (
              <div className="gallery-empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', textAlign: 'center', padding: '2rem', color: '#facc15' }}>
                <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>It's empty here</p>
                <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No lesson selected.</p>
                <p style={{ marginBottom: '1rem' }}>Switch to gallery view to open a lesson, or create a new one.</p>
                {currentRole === 'manager' && (
                <button
                  className="modal-button primary"
                  onClick={() => { setGalleryViewMode(true); setShowAddTemplateModal(true); }}
                >
                  Create lesson
                </button>
                )}
              </div>
            ) : (
              <>
            {/* Only show full-screen overlay when viewing the lesson that's being created; when user switched to another lesson, let that iframe load without blocking */}
            {isLessonLoading && !lessonError && (!isCreatingTemplate || selectedTemplate === pendingTemplateId) && (
              <div className="lesson-loading-overlay">
                <div className="lesson-loading-spinner">
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                </div>
                    <div className="lesson-loading-text">
                      {isCreatingTemplate && creatingTemplateName && selectedTemplate === pendingTemplateId
                        ? `Creating "${creatingTemplateName}"...` 
                        : 'Loading lesson...'}
                    </div>
                  </div>
                )}
                {lessonError && (
                  <div className="lesson-error-overlay">
                    <div className="lesson-error-icon">!</div>
                    <div className="lesson-error-title">Error Loading Lesson</div>
                    <div className="lesson-error-message">{lessonError}</div>
                  <button
                  className="lesson-error-retry"
                  onClick={() => {
                    setLessonError(null);
                    setIsLessonLoading(true);
                    if (iframeRef.current) {
                      const currentSrc = iframeRef.current.src;
                      iframeRef.current.src = '';
                      setTimeout(() => {
                        if (iframeRef.current) {
                          iframeRef.current.src = currentSrc;
                        }
                      }, 100);
                    }
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            <div className="floating-lesson-actions">
              {!galleryViewMode && (
                <>
                {availableTemplates.find(t => t.id === selectedTemplate)?.is_assessment && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    background: '#6366f1', color: '#fff', letterSpacing: '0.03em',
                    boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
                  }}>📝 Assessment</span>
                )}
{(templateOwnership[selectedTemplate]?.is_owner ?? true) && (['manager', 'creator'].includes(currentLessonRole) || currentRole === 'manager') && (
<button
  className={`icon-btn round action-edit ${isEditMode ? 'active' : ''}`}
  onClick={() => setIsEditMode(!isEditMode)}
  title={isEditMode ? 'Exit edit mode' : 'Edit lesson content'}
  disabled={!selectedTemplate || !(templateOwnership[selectedTemplate]?.is_owner ?? true)}
>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
  <span className="sr-only">{isEditMode ? 'Exit edit mode' : 'Edit content'}</span>
</button>
)}
{(currentLessonRole === 'distributor' || currentLessonRole === 'manager' || currentRole === 'manager') && (
  <button
    className="icon-btn round action-deploy"
    onClick={handleDeployClick}
    disabled={isDeploying}
    title={existingDeployment ? "Redeploy to Cloudflare Pages (will create new link if file changed)" : "Deploy to Cloudflare Pages"}
  >
    {isDeploying ? (
      <div className="deploy-spinner"></div>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
      </svg>
    )}
    <span className="sr-only">{existingDeployment ? 'Redeploy' : 'Deploy'}</span>
  </button>
)}
                  {['manager', 'creator'].includes(currentLessonRole) && <button className="icon-btn round action-save" onClick={handleSave} title="Save inputs">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    <span className="sr-only">Save</span>
                  </button>}
                  
                  {['manager', 'creator'].includes(currentLessonRole) && <button className="icon-btn round action-clear" onClick={handleClear} title="Clear saved state">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                    </svg>
                    <span className="sr-only">Clear</span>
                  </button>}
                  {['manager', 'creator', 'final_approver'].includes(currentLessonRole) && <button className="icon-btn round action-qa" onClick={handleOpenQaFeedback} style={{backgroundColor: '#8b5cf6', color: 'white', border: '1px solid #7c3aed', animation: qaRunning ? 'qa-btn-pulse 1.2s ease-in-out infinite' : 'none'}} title={qaRunning ? 'QA evaluation running…' : 'QA Report'}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    <span className="sr-only">QA Report</span>
                  </button>}
                </>
              )}
              <button 
                className="icon-btn round action-fullscreen"
                onClick={handleFullscreen}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <line x1="3" y1="3" x2="9" y2="9"></line>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="21" y1="21" x2="15" y2="15"></line>
                    <polyline points="21 9 21 3 15 3"></polyline>
                    <line x1="21" y1="3" x2="15" y2="9"></line>
                    <polyline points="3 15 3 21 9 21"></polyline>
                    <line x1="3" y1="21" x2="9" y2="15"></line>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <line x1="3" y1="3" x2="9" y2="9"></line>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="21" y1="21" x2="15" y2="15"></line>
                  </svg>
                )}
                <span className="sr-only">{isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}</span>
              </button>
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round action-code"
                onClick={() => setShowCodePreview(true)}
                title="View code"
                disabled={!selectedTemplate}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
                <span className="sr-only">View code</span>
              </button>}
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round action-copy"
                onClick={() => handleCopyClick(selectedTemplate)}
                title={`Copy ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                disabled={!selectedTemplate}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                    <span className="sr-only">Copy lesson</span>
                      </button>}
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round danger action-delete"
                onClick={() => handleDeleteClick(selectedTemplate)}
                title={`Delete ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                disabled={!selectedTemplate || !(templateOwnership[selectedTemplate]?.is_owner ?? true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                  <path d="M10 11v6"></path>
                  <path d="M14 11v6"></path>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                </svg>
                <span className="sr-only">Delete lesson</span>
              </button>}
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round action-keys"
                onClick={() => setShowKeysModal(true)}
                title="API Keys (Google, OpenAI, Anthropic, DeepSeek)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="7" cy="12" r="3"></circle>
                  <path d="M10 12h11"></path>
                  <path d="M18 12v3"></path>
                  <path d="M15 12v2"></path>
                </svg>
                <span className="sr-only">API Keys</span>
              </button>}
            </div>
                <div className={`lesson-viewport ${isFullscreen ? 'fullscreen' : ''}`}>
                  {pendingTemplateId && selectedTemplate === pendingTemplateId ? (
                    <div className="lesson-iframe pending" aria-label="Lesson loading"></div>
                  ) : (
                  // REPLACE WITH:
  <>
    <div style={{
      display: 'flex',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* Lesson iframe */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <iframe
          ref={iframeRef}
          title="AI Lesson"
          src={buildIframeSrc(selectedTemplate, iframeNonce)}
          className="lesson-iframe"
          key={`${selectedTemplate}-${iframeNonce}`}
        />
        <LessonIframe
          iframeRef={iframeRef}
          lessonFrameRef={lessonFrameRef}
          setIsLessonLoading={setIsLessonLoading}
          selectedTemplate={selectedTemplate}
          currentPageId={currentPageId}
        />
      </div>

      {['reviewer', 'final_approver', 'creator', 'manager'].includes(currentLessonRole) && selectedTemplate && (
        <ReviewSidebar
          templateId={selectedTemplate}
          currentPageId={currentPageId}
          token={currentToken}
          role={currentLessonRole}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      )}
      {currentLessonRole === 'distributor' && selectedTemplate && (
        <DistributorTeamPanel
          templateId={selectedTemplate}
          token={currentToken}
        />
      )}
    </div>

    
  </>
)}
            </div>
              </>
            )}
          </div>
        </div>
      </div>
      
          {/* Deploy Confirm Modal */}
          {showDeployConfirmModal && (
            <div className="modal-overlay" onClick={() => setShowDeployConfirmModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Deploy Lesson</h2>
                  <button className="modal-close" onClick={() => setShowDeployConfirmModal(false)}>
                    ×
                  </button>
                </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                Deploy <strong>{availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}</strong> to Cloudflare Pages?
              </p>
              {existingDeployment && (
                <div className="deployment-meta">
                  {existingDeployment.deployed_at && (
                    <div className="deployment-meta-row">
                      <span>Last deployed:</span>
                      <span>{formatDeployTime(existingDeployment.deployed_at)}</span>
                    </div>
                  )}
                  {existingDeployment.deployment_url && (
                    <a href={existingDeployment.deployment_url} target="_blank" rel="noreferrer">
                      {existingDeployment.deployment_url}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowDeployConfirmModal(false)}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={handleDeployConfirm}>
                Deploy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Status Modal */}
      {showDeployModal && (
        <div className="modal-overlay" onClick={() => { if (!isDeploying) setShowDeployModal(false); }}>
          <div className="modal-content deploy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cloudflare Deployment</h2>
              <button
                className="modal-close"
                onClick={() => { if (!isDeploying) setShowDeployModal(false); }}
                disabled={isDeploying}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
                  {isDeploying ? (
                    <div className="deployment-progress">
                      <div className="deploy-spinner large"></div>
                      <h3>Deploying...</h3>
                      <p>Uploading your lesson to Cloudflare Pages.</p>
                    </div>
                  ) : deploymentStatus === 'success' && deploymentUrl ? (
                    <div className="deployment-success">
                  <div className="status-icon">✓</div>
                      <h3>Deployment successful</h3>
                      <p>Your lesson is live.</p>
                      <a href={deploymentUrl} target="_blank" rel="noreferrer">
                        Open deployment
                      </a>
                  {existingDeployment?.production_url && existingDeployment.production_url !== deploymentUrl && (
                    <a href={existingDeployment.production_url} target="_blank" rel="noreferrer">
                      Open production URL
                    </a>
                  )}
                </div>
              ) : deploymentStatus === 'error' ? (
                <div className="deployment-error">
                  <div className="status-icon">!</div>
                  <h3>Deployment failed</h3>
                  <p>Please check your Cloudflare credentials and project settings.</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
<div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '360px' }}>
  {toasts.map(toast => (
    <div key={toast.id} style={{
      background: toast.type === 'success' ? '#059669' : '#3b82f6',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      fontSize: '14px',
      lineHeight: '1.4',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      animation: 'fadeInUp 0.3s ease',
    }}>
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0, opacity: 0.8 }}>×</button>
    </div>
  ))}
</div>


      {showInlineAlert && (
        <div className="modal-overlay" onClick={() => setShowInlineAlert(false)}>
          <div className="modal-content inline-alert" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Notice</h2>
              <button className="modal-close" onClick={() => setShowInlineAlert(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                {inlineAlertMessage}
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-button primary" onClick={() => setShowInlineAlert(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Modal */}
      {showKeysModal && (
        <div className="modal-overlay" onClick={() => setShowKeysModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>API Keys</h2>
              <button className="modal-close" onClick={() => setShowKeysModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Add your keys to use your own API quotas. They stay on this device.
              </p>
              <div className="form-group">
                <label htmlFor="api-key-google">Google (Gemini)</label>
                <input
                  id="api-key-google"
                  type="password"
                  value={apiKeyInputs.google}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, google: e.target.value }))}
                  placeholder="AIza..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-openai">OpenAI (GPT)</label>
                <input
                  id="api-key-openai"
                  type="password"
                  value={apiKeyInputs.openai}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, openai: e.target.value }))}
                  placeholder="sk-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-anthropic">Anthropic (Claude)</label>
                <input
                  id="api-key-anthropic"
                  type="password"
                  value={apiKeyInputs.anthropic}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, anthropic: e.target.value }))}
                  placeholder="sk-ant-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-deepseek">DeepSeek (V4)</label>
                <input
                  id="api-key-deepseek"
                  type="password"
                  value={apiKeyInputs.deepseek}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, deepseek: e.target.value }))}
                  placeholder="sk-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowKeysModal(false)}>Cancel</button>
              <button className="modal-button primary" onClick={saveApiKeys}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddTemplateModal && (
            <div className="modal-overlay" onClick={() => {
              if (!isCreatingTemplate) {
                setShowAddTemplateModal(false);
                setNewTemplateModel('gpt-5.6-luna');
                setNewTemplateImages([]);
                setNewTemplateImagePreviews([]);
                setNewTemplateIsAssessment(false);
                setNewTemplateAssessmentSource('topic');
                setNewTemplateAssessmentTopic('');
                setNewTemplateAssessmentTemplateIds('');
                setNewTemplateQuestionCount('');
              }
            }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Lesson</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  if (!isCreatingTemplate) {
                    setShowAddTemplateModal(false);
                    setNewTemplateModel('gpt-5.6-luna');
                    setNewTemplateImages([]);
                    setNewTemplateImagePreviews([]);
                    setNewTemplateIsAssessment(false);
                    setNewTemplateAssessmentSource('topic');
                    setNewTemplateAssessmentTopic('');
                    setNewTemplateAssessmentTemplateIds('');
                    setNewTemplateQuestionCount('');
                  }
                }}
                disabled={isCreatingTemplate}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
                  {isCreatingTemplate ? (
                    <div className="template-creating">
                  <div className="creating-spinner">
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                  </div>
                      <h3>Creating Lesson...</h3>
                      <p>AI is generating your lesson based on your description. This may take a moment.</p>
                      <div className="creating-steps">
                    <div className="step-item">
                      <span className="step-icon">✓</span>
                      <span>Analyzing your requirements</span>
                    </div>
                    <div className="step-item">
                      <span className="step-icon">…</span>
                      <span>Generating HTML code</span>
                    </div>
                        <div className="step-item">
                          <span className="step-icon">…</span>
                          <span>Creating lesson file</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* ── Assessment toggle — FIRST ── */}
                  <div className="form-group">
                    <div style={{
                      background: newTemplateIsAssessment ? 'linear-gradient(135deg, #fef3c7 0%, #ede9fe 100%)' : '#f8fafc',
                      border: newTemplateIsAssessment ? '1.5px solid #a78bfa' : '1.5px solid #e2e8f0',
                      borderRadius: '12px', padding: '14px', transition: 'all 0.2s ease',
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                          background: newTemplateIsAssessment ? '#6366f1' : '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}>
                          {newTemplateIsAssessment && <span style={{ color: '#fff', fontSize: '13px' }}>✓</span>}
                          <input type="checkbox" checked={newTemplateIsAssessment}
                            onChange={e => {
                              setNewTemplateIsAssessment(e.target.checked);
                              setNewTemplateAssessmentSource('topic');
                              setNewTemplateAssessmentTopic('');
                              setNewTemplateAssessmentTemplateIds('');
                              setNewTemplateQuestionCount('');
                            }}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: newTemplateIsAssessment ? '#4c1d95' : '#374151' }}>
                            📝 Assessment Only
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            Quiz-only — questions derived from objectives, no teaching slides
                          </div>
                        </div>
                      </label>
                      {newTemplateIsAssessment && (
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(167,139,250,0.3)', paddingTop: '14px' }}>
                          <div style={{ display: 'flex', gap: '6px', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                            <button type="button" onClick={() => setNewTemplateAssessmentSource('topic')} disabled={isCreatingTemplate}
                              style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'topic' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'topic' ? '#d97706' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'topic' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                              🎯 By Topic
                            </button>
                            <button type="button" onClick={() => setNewTemplateAssessmentSource('template_ids')} disabled={isCreatingTemplate}
                              style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'template_ids' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'template_ids' ? '#6366f1' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'template_ids' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                              📚 From Lessons
                            </button>
                          </div>
                          {newTemplateAssessmentSource === 'topic' ? (
                            <input value={newTemplateAssessmentTopic} onChange={e => setNewTemplateAssessmentTopic(e.target.value)}
                              placeholder="e.g. Workplace Safety, Python Basics…" disabled={isCreatingTemplate}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #ddd6fe', fontSize: '13px', boxSizing: 'border-box', outline: 'none', background: '#fafafa' }} />
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{ border: '1.5px solid #ddd6fe', borderRadius: '8px', background: '#fafafa', maxHeight: '160px', overflowY: 'auto' }}>
                                {availableTemplates.filter(t => !t.id.startsWith('creating-')).map(t => {
                                  const selected = newTemplateAssessmentTemplateIds.split(',').map(s => s.trim()).filter(Boolean).includes(t.id);
                                  return (
                                    <div key={t.id} onClick={() => { if (isCreatingTemplate) return;
                                        const ids = newTemplateAssessmentTemplateIds.split(',').map(s => s.trim()).filter(Boolean);
                                        setNewTemplateAssessmentTemplateIds((selected ? ids.filter(id => id !== t.id) : [...ids, t.id]).join(', ')); }}
                                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                                        background: selected ? '#ede9fe' : 'transparent', borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}>
                                      <span style={{ width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, background: selected ? '#6366f1' : '#e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff' }}>
                                        {selected ? '✓' : ''}
                                      </span>
                                      <span style={{ color: '#374151', fontWeight: selected ? 600 : 400 }}>{t.name}</span>
                                      <span style={{ color: '#9ca3af', marginLeft: 'auto', fontSize: '11px' }}>{t.id}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <small style={{ color: '#9ca3af', fontSize: '11px' }}>Smart objectives are extracted from selected lessons to shape the questions.</small>
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>Number of questions:</label>
                            <input type="number" min={5} max={50} value={newTemplateQuestionCount} onChange={e => setNewTemplateQuestionCount(e.target.value)}
                              placeholder="auto" disabled={isCreatingTemplate}
                              style={{ width: '80px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Blank = 2× objectives</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                      {/* ── Description — only for non-assessment ── */}
                      {!newTemplateIsAssessment && (
                      <div className="form-group">
                      <label htmlFor="template-description">Vibe your lesson here</label>
                      <textarea
                        id="template-description"
                        value={newTemplateDescription}
                        onChange={(e) => setNewTemplateDescription(e.target.value)}
                        placeholder="Describe the lesson you want (topic, level, objectives, interactions, tone)."
                        disabled={isCreatingTemplate}
                        className="form-textarea"
                        rows={5}
                      />
                      </div>
                      )}
                  <div className="form-group">
                    <label htmlFor="template-model">AI Model:</label>
                    <select
                      id="template-model"
                      value={enabledModelIds.includes(newTemplateModel) ? newTemplateModel : (enabledModelIds[0] || OPENAI_MODEL_ID)}
                      onChange={(e) => setNewTemplateModel(e.target.value)}
                      disabled={isCreatingTemplate}
                      className="form-input"
                      style={{ 
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--panel-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <option value="gpt-5.6-luna" disabled={!isModelEnabled('gpt-5.6-luna')}>GPT 5.6 Luna</option>
                      <option value="gpt-5.6-terra" disabled={!isModelEnabled('gpt-5.6-terra')}>GPT 5.6 Terra</option>
                      <option value="gemini-3.5-flash" disabled={!isModelEnabled('gemini-3.5-flash')}>Google Gemini 3.5 Flash</option>
                      <option value={OPENAI_MODEL_ID} disabled={!isModelEnabled(OPENAI_MODEL_ID)}>GPT 5.4</option>
                      <option value="claude-sonnet-4-5" disabled={!isModelEnabled('claude-sonnet-4-5')}>Claude Sonnet 4.5</option>
                      <option value="deepseek-v4-flash" disabled={!isModelEnabled('deepseek-v4-flash')}>DeepSeek V4 Flash</option>
                      <option value="deepseek-v4-pro" disabled={!isModelEnabled('deepseek-v4-pro')}>DeepSeek V4 Pro</option>
                      {process.env.REACT_APP_SHOW_OLLAMA_MODELS === 'true' && <>
  <option value="ollama-qwen3.5-397b" disabled={!isModelEnabled('ollama-qwen3.5-397b')}>Ollama: Qwen 3.5 397B</option>
  <option value="ollama-kimi-k2.5" disabled={!isModelEnabled('ollama-kimi-k2.5')}>Ollama: Kimi K2.5</option>
  <option value="ollama-qwen3-coder-next" disabled={!isModelEnabled('ollama-qwen3-coder-next')}>Ollama: Qwen3 Coder Next</option>
  <option value="ollama-gemma4-31b" disabled={!isModelEnabled('ollama-gemma4-31b')}>Ollama: Gemma4 31B</option>
</>}
                    </select>
                        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                          Select which AI model to use for generating your lesson
                        </small>
                      </div>
                      {/* ── OLD ASSESSMENT TOGGLE PLACEHOLDER (removed, moved to top) ── */}
                  <div className="form-group" style={{display:'none'}}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                          background: newTemplateIsAssessment ? '#6366f1' : '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}>
                          {newTemplateIsAssessment && <span style={{ color: '#fff', fontSize: '13px' }}>✓</span>}
                          <input
                            type="checkbox"
                            checked={newTemplateIsAssessment}
                            onChange={e => {
                              setNewTemplateIsAssessment(e.target.checked);
                              setNewTemplateAssessmentSource('topic');
                              setNewTemplateAssessmentTopic('');
                              setNewTemplateAssessmentTemplateIds('');
                              setNewTemplateQuestionCount('');
                            }}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: newTemplateIsAssessment ? '#4c1d95' : '#374151' }}>
                            📝 Assessment Only
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            Quiz-only lesson — no teaching slides
                          </div>
                        </div>
                      </label>

                      {newTemplateIsAssessment && (
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(167,139,250,0.3)', paddingTop: '14px' }}>
                          
                          {/* Source toggle */}
                          <div style={{ display: 'flex', gap: '6px', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                            <button
                              type="button"
                              onClick={() => setNewTemplateAssessmentSource('topic')}
                              disabled={isCreatingTemplate}
                              style={{
                                flex: 1, padding: '7px', borderRadius: '6px', border: 'none',
                                cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'topic' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'topic' ? '#d97706' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'topic' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.15s',
                              }}
                            >
                              🎯 By Topic
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewTemplateAssessmentSource('template_ids')}
                              disabled={isCreatingTemplate}
                              style={{
                                flex: 1, padding: '7px', borderRadius: '6px', border: 'none',
                                cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'template_ids' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'template_ids' ? '#6366f1' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'template_ids' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.15s',
                              }}
                            >
                              📚 From Lessons
                            </button>
                          </div>

                          {/* Conditional input */}
                          {newTemplateAssessmentSource === 'topic' ? (
                            <input
                              value={newTemplateAssessmentTopic}
                              onChange={e => setNewTemplateAssessmentTopic(e.target.value)}
                              placeholder="e.g. Workplace Safety, Python Basics…"
                              disabled={isCreatingTemplate}
                              style={{
                                width: '100%', padding: '9px 12px', borderRadius: '8px',
                                border: '1.5px solid #ddd6fe', fontSize: '13px',
                                boxSizing: 'border-box', outline: 'none',
                                background: '#fafafa',
                              }}
                            />
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{
                                border: '1.5px solid #ddd6fe', borderRadius: '8px',
                                background: '#fafafa', maxHeight: '160px', overflowY: 'auto',
                              }}>
                                {availableTemplates.filter(t => !t.id.startsWith('creating-')).map(t => {
                                  const selected = newTemplateAssessmentTemplateIds
                                    .split(',').map(s => s.trim()).filter(Boolean).includes(t.id);
                                  return (
                                    <div
                                      key={t.id}
                                      onClick={() => {
                                        if (isCreatingTemplate) return;
                                        const ids = newTemplateAssessmentTemplateIds
                                          .split(',').map(s => s.trim()).filter(Boolean);
                                        const next = selected
                                          ? ids.filter(id => id !== t.id)
                                          : [...ids, t.id];
                                        setNewTemplateAssessmentTemplateIds(next.join(', '));
                                      }}
                                      style={{
                                        padding: '8px 12px', cursor: 'pointer', fontSize: '12px',
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        background: selected ? '#ede9fe' : 'transparent',
                                        borderBottom: '1px solid #f1f5f9',
                                        transition: 'background 0.1s',
                                      }}
                                    >
                                      <span style={{
                                        width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                                        background: selected ? '#6366f1' : '#e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '10px', color: '#fff',
                                      }}>
                                        {selected ? '✓' : ''}
                                      </span>
                                      <span style={{ color: '#374151', fontWeight: selected ? 600 : 400 }}>{t.name}</span>
                                      <span style={{ color: '#9ca3af', marginLeft: 'auto', fontSize: '11px' }}>{t.id}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <small style={{ color: '#9ca3af', fontSize: '11px' }}>
                                Smart objectives are extracted from selected lessons to shape the questions.
                              </small>
                            </div>
                          )}

                          {/* Question count */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                              Number of questions:
                            </label>
                            <input
                              type="number"
                              min={5} max={50}
                              value={newTemplateQuestionCount}
                              onChange={e => setNewTemplateQuestionCount(e.target.value)}
                              placeholder="12–15"
                              disabled={isCreatingTemplate}
                              style={{
                                width: '80px', padding: '6px 8px', borderRadius: '6px',
                                border: '1px solid #d1d5db', fontSize: '13px',
                              }}
                            />
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                              Leave blank for default (12–15)
                            </span>
                          </div>

                        </div>
                      )}
                    </div>
                  </div>
                  {!newTemplateIsAssessment && <div className="form-group">
                    <label htmlFor="template-image">Reference Images (Optional, up to 6):</label>
                    <input
                      type="file"
                      id="template-image"
                      ref={templateImageInputRef}
                      accept="image/*"
                      multiple
                      onChange={handleTemplateImageSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateImageInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        🖼️ Choose Image(s)
                      </button>
                      {newTemplateImagePreviews.length > 0 && newTemplateImagePreviews.map((src, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={src}
                            alt={`Preview ${idx + 1}`}
                            style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <button
                            type="button"
                            onClick={() => removeTemplateImage(idx)}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', top: '2px', right: '2px', padding: '2px 6px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload one or more images as design inspiration for the template
                    </small>
                  </div>}
                  {!newTemplateIsAssessment && <div className="form-group">
                    <label htmlFor="template-file">Data File (CSV/Excel/PDF/DOCX/PPTX) - Optional:</label>
                    <input
                      type="file"
                      id="template-file"
                      ref={templateFileInputRef}
                      accept=".csv,.xlsx,.xls,.pdf,.docx,.pptx"
                      onChange={handleTemplateFileSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateFileInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        📁 Choose File
                      </button>
                      {newTemplateFile && (
                        <>
                          <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                            📄 {newTemplateFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={removeTemplateFile}
                            disabled={isCreatingTemplate}
                            style={{ padding: '4px 8px', cursor: 'pointer' }}
                          >
                            × Remove
                          </button>
                        </>
                      )}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload a CSV, Excel, PDF, Word, or PowerPoint file to incorporate data into the template
                    </small>
                  </div>}
                      <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={() => {
                        setShowAddTemplateModal(false);
                        setNewTemplateModel('gpt-5.6-luna');
                        setNewTemplateImages([]);
                        setNewTemplateImagePreviews([]);
                        setNewTemplateIsAssessment(false);
                        setNewTemplateAssessmentSource('topic');
                        setNewTemplateAssessmentTopic('');
                        setNewTemplateAssessmentTemplateIds('');
                        setNewTemplateQuestionCount('');
                      }}
                      disabled={isCreatingTemplate}
                    >
                      Cancel
                    </button>
                        <button
                          className="modal-button primary"
                          onClick={handleCreateTemplate}
                          disabled={isCreatingTemplate || isUploadingFile ||
                            (!newTemplateIsAssessment && !newTemplateDescription.trim()) ||
                            (newTemplateIsAssessment && newTemplateAssessmentSource === 'topic' && !newTemplateAssessmentTopic.trim()) ||
                            (newTemplateIsAssessment && newTemplateAssessmentSource === 'template_ids' && !newTemplateAssessmentTemplateIds.trim())}
                        >
                          {isUploadingFile ? 'Uploading file…' : 'Create lesson'}
                        </button>
                      </div>
                    </>
                  )}
            </div>
          </div>
        </div>
      )}

          {/* Copy lesson Modal */}
          {showCopyModal && (
        <div className="modal-overlay" onClick={() => {
          if (!isCopying) {
            setShowCopyModal(false);
            setCopyLessonName('');
          }
        }}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Copy lesson</h2>
                  <button 
                    className="modal-close"
                onClick={() => {
                  if (!isCopying) {
                    setShowCopyModal(false);
                    setCopyLessonName('');
                  }
                }}
                    disabled={isCopying}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                    Create a copy of this lesson that you can edit. The original lesson will remain unchanged.
                  </p>
                  <div className="form-group">
                    <label htmlFor="copy-lesson-name">New Lesson Name:</label>
                    <input
                      id="copy-lesson-name"
                      type="text"
                      value={copyLessonName}
                      onChange={(e) => setCopyLessonName(e.target.value)}
                      placeholder="Enter a name for the copied lesson"
                      disabled={isCopying}
                      className="form-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCopying && copyLessonName.trim()) {
                      handleCopyLesson();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
                  <button
                className="modal-button cancel"
                onClick={() => {
                  setShowCopyModal(false);
                  setCopyLessonName('');
                }}
                disabled={isCopying}
              >
                Cancel
              </button>
              <button
                className="modal-button primary"
                onClick={handleCopyLesson}
                    disabled={isCopying || !copyLessonName.trim()}
                  >
                    {isCopying ? 'Copying...' : 'Copy lesson'}
                  </button>
                </div>
              </div>
            </div>
          )}

      {/* QA Feedback Modal */}
          {showQaModal && (
            <div className="modal-overlay" onClick={() => setShowQaModal(false)}>
              <div className="modal-content qa-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>QA Feedback Report</h2>
                  <button className="modal-close" onClick={() => setShowQaModal(false)}>×</button>
                </div>
                <div className="modal-body" style={{ textAlign: 'left' }}>
                  {isQaLoading ? (
                    <div className="qa-loading-container">
                      <div className="deploy-spinner large"></div>
                      <p>Analyzing lesson performance...</p>
                    </div>
                  ) : qaReportData?.status === 'generating' ? (
                    <div className="qa-loading-container">
                      <div className="deploy-spinner large"></div>
                      <p>Report is being generated in the background. Please come back in a minute.</p>
                    </div>
                  ) : qaReportData?.error ? (
                    <div className="qa-error-message">
                      <p>{qaReportData.error}</p>
                      {qaReportData.status === "not_found" && ['manager', 'creator'].includes(currentLessonRole) && (
                        <button
                          className="modal-button primary"
                          style={{ marginTop: '15px' }}
                          onClick={async () => {
                            setIsQaLoading(true);
                            try {
                              await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
                              setQaReportData({ status: 'generating', error: "Evaluation started in background. Please check back in a minute." });
                              startQaPolling(selectedTemplate);
                            } catch (err) {
                              setIsQaLoading(false);
                              setQaReportData({ status: 'error', error: "Failed to start evaluation." });
                            }
                          }}
                        >
                          Run Evaluation Now
                        </button>
                      )}
                    </div>
                  ) : qaReportData ? (
                    <div className="qa-report-container">
                      {(() => {
                        const currentScore = qaReportData.overall_score || 0;
                        const fixedPoints = Object.values(qaReportData.sections || {}).reduce((sum, sec) => {
                          const fixedImps = (sec.improvements || []).filter(imp => imp._fixed);
                          return sum + fixedImps.reduce((s, imp) => s + (imp.estimated_points || 0), 0);
                        }, 0);
                        const originalScore = Math.max(0, currentScore - fixedPoints);

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', margin: '8px 0 4px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em', marginBottom: '6px', color: 'var(--muted, #94a3b8)' }}>
                                <span>Current Score: <strong style={{ color: 'var(--text-color, #e2e8f0)' }}>{currentScore}/100</strong>
                                  {fixedPoints > 0 && <span style={{ marginLeft: '8px', color: '#22c55e', fontSize: '0.9em' }}>(Original: {originalScore}, +{fixedPoints} pts added)</span>}
                                </span>
                              </div>
                              <div style={{ height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                                <div style={{ position: 'relative', width: `${currentScore}%`, height: '100%', background: currentScore >= 80 ? '#22c55e' : currentScore >= 50 ? '#eab308' : '#ef4444', borderRadius: '4px', transition: 'width 0.5s ease' }}></div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {qaReportData.summary && (
                        <div className="qa-section">
                          <h3>Summary</h3>
                          <p className="qa-summary-text" dangerouslySetInnerHTML={{ __html: qaReportData.summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}></p>
                        </div>
                      )}
                      {qaReportData.sections && Object.keys(qaReportData.sections).length > 0 && (
                        <div className="qa-section">
                          <h3>Section Scores</h3>
                          <div style={{ display: 'grid', gap: '10px' }}>
                            {Object.entries(qaReportData.sections).map(([key, sec]) => {
                              const label = { educational_structure: 'Educational Structure', interactivity: 'Interactivity', ui_ux: 'UI/UX & Readability', images_and_media: 'Images & Media', content_accuracy: 'Content Accuracy' }[key] || key;
                              const pct = sec.max > 0 ? Math.round((sec.score / sec.max) * 100) : 0;
                              const barColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
                              return (
                                <div key={key} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '10px 14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontWeight: 600, fontSize: '0.9em' }}>
                                    <span>{label}</span>
                                    <span style={{ color: barColor }}>{sec.score}/{sec.max}</span>
                                  </div>
                                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.5s ease' }}></div>
                                  </div>
                                  {sec.section_summary && (
                                    <p style={{ margin: '0 0 8px 0', fontSize: '0.85em', fontStyle: 'italic', color: 'var(--text-color, #e2e8f0)' }}>
                                      {sec.section_summary}
                                    </p>
                                  )}
                                  {sec.findings && sec.findings.length > 0 && (
                                    <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 16px', fontSize: '0.82em', color: '#cbd5e1' }}>
                                      {sec.findings.map((f, i) => (
                                        <li key={i} style={{ marginBottom: '2px' }}>
                                          {f.split(/(Page\s+\d+)/gi).map((part, j) =>
                                            /^Page\s+\d+$/i.test(part)
                                              ? <strong key={j} style={{ color: '#e2e8f0' }}>{part}</strong>
                                              : part
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {sec.improvements && sec.improvements.length > 0 && ['manager', 'creator'].includes(currentLessonRole) && (
                                    <div style={{ marginTop: '10px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: '8px', padding: '10px 12px' }}>
                                      <div style={{ fontSize: '0.82em', fontWeight: 600, color: '#22c55e', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>💡</span> How to improve this score
                                      </div>
                                      {sec.improvements.map((imp) => (
                                        <div key={imp.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', gap: '8px', opacity: imp._fixed ? 0.6 : 1 }}>
                                          <div style={{ flex: 1, fontSize: '0.82em' }}>
                                            <span style={{ marginRight: '6px' }}>{imp.type === 'fix' ? '🔧' : '✨'}</span>
                                            {imp.description}
                                            {(() => {
                                              const pages = [...new Set([...(imp.description || ''), ...(imp.fix_prompt || '')].join(' ').match(/Page\s+\d+/gi) || [])];
                                              return pages.length > 0 ? (
                                                <span style={{ marginLeft: '6px' }}>
                                                  {pages.map(p => <span key={p} style={{ display: 'inline-block', fontSize: '0.78em', fontWeight: 800, color: '#e2e8f0', background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: '4px', padding: '1px 5px', marginLeft: '3px' }}>{p}</span>)}
                                                </span>
                                              ) : null;
                                            })()}
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                            <span style={{ fontSize: '0.75em', fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.12)', padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>+{imp.estimated_points} pts</span>
                                            <button
                                              className="qa-fix-button"
                                              style={{ fontSize: '0.75em', padding: '3px 10px', ...(imp._fixed ? { background: 'transparent', cursor: 'default', color: '#22c55e', border: '1px solid #22c55e' } : {}) }}
                                              disabled={fixingImprovementId !== null || batchFixingSection !== null || imp._fixed}
                                              onClick={async () => {
                                                setFixingImprovementId(imp.id);
                                                const fixTemplate = selectedTemplate;
                                                try {
                                                  const res = await fetch(
                                                    `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-fix`,
                                                    withAuthFetchOptions({
                                                      method: 'POST',
                                                      headers: { 'Content-Type': 'application/json' },
                                                      body: JSON.stringify({
                                                        fix_prompt: imp.fix_prompt,
                                                        issue_id: imp.id,
                                                        model: availableTemplates.find(t => t.id === fixTemplate)?.default_model || 'gpt-5.4-mini'
                                                      }),
                                                    })
                                                  );
                                                  const startData = await res.json();
                                                  if (!startData.success) { setFixingImprovementId(null); return; }
                                                  const pollInterval = setInterval(async () => {
                                                    try {
                                                      const statusRes = await fetch(
                                                        `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-fix-status/${encodeURIComponent(imp.id)}`,
                                                        withAuthFetchOptions()
                                                      );
                                                      const statusData = await statusRes.json();
                                                      if (statusData.status === 'done') {
                                                        clearInterval(pollInterval);
                                                        if (selectedTemplateRef.current === fixTemplate && iframeRef.current) {
                                                          iframeRef.current.src = buildIframeSrc(fixTemplate, Date.now());
                                                        }
                                                        setQaReportData(prev => {
                                                          if (!prev) return prev;
                                                          const newSections = { ...prev.sections };
                                                          let ptsAdded = 0;
                                                          if (newSections[key]) {
                                                            const targetImp = (newSections[key].improvements || []).find(i => i.id === imp.id);
                                                            ptsAdded = targetImp && !targetImp._fixed ? (targetImp.estimated_points || 0) : 0;
                                                            newSections[key] = { ...newSections[key], improvements: (newSections[key].improvements || []).map(i => i.id === imp.id ? { ...i, _fixed: true } : i) };
                                                            newSections[key].score = Math.min(newSections[key].max || 20, (newSections[key].score || 0) + ptsAdded);
                                                          }
                                                          const newScore = Math.min(100, (prev.overall_score || 0) + ptsAdded);
                                                          
                                                          // Sync with flat issues list
                                                          const newIssues = (prev.issues || []).map(issue => {
                                                            if (issue.related_improvement_id === imp.id || (imp.related_issue_ids && imp.related_issue_ids.includes(issue.id))) {
                                                              return { ...issue, _fixed: true };
                                                            }
                                                            return issue;
                                                          });

                                                          const updatedReport = { ...prev, overall_score: newScore, sections: newSections, issues: newIssues };
                                                          
                                                          // Real-time success re-evaluation
                                                          const hasUnfixedMajor = updatedReport.issues.some(i => !i._fixed && (i.severity === 'critical' || i.severity === 'major'));
                                                          updatedReport.success = updatedReport.overall_score >= 80 && !hasUnfixedMajor;
                                                          
                                                          return updatedReport;
                                                        });
                                                        setFixingImprovementId(null);
                                                        window.dispatchEvent(new CustomEvent('qaFixApplied', {
                                                          detail: { 
                                                            template: fixTemplate, 
                                                            userText: imp.fix_prompt, 
                                                            advisorText: statusData.ai_response_text || "Fix applied successfully."
                                                          }
                                                        }));
                                                        window.dispatchEvent(new CustomEvent('qa_fix_applied'));
                                                      } else if (statusData.status === 'error') {
                                                        clearInterval(pollInterval);
                                                        setFixingImprovementId(null);
                                                      }
                                                    } catch { }
                                                  }, 3000);
                                                } catch { setFixingImprovementId(null); }
                                              }}
                                            >
                                              {imp._fixed ? '✅ Fixed' : fixingImprovementId === imp.id ? (
                                                <><div className="deploy-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', display: 'inline-block', marginRight: '4px' }}></div>Fixing...</>
                                              ) : 'Fix'}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                      {false && sec.improvements.filter(imp => !imp._fixed).length > 1 && (
                                        <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                          <button
                                            className="qa-fix-button"
                                            style={{ fontSize: '0.78em', padding: '4px 14px', background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}
                                            disabled={batchFixingSection !== null || fixingImprovementId !== null}
                                            onClick={async () => {
                                              setBatchFixingSection(key);
                                              setBatchFixProgress({ total: sec.improvements.filter(i => !i._fixed).length, completed: 0 });
                                              const fixTemplate = selectedTemplate;
                                              try {
                                                const res = await fetch(
                                                  `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-batch-fix`,
                                                  withAuthFetchOptions({
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                      section_key: key,
                                                      improvements: sec.improvements.filter(i => !i._fixed).map(i => ({ id: i.id, fix_prompt: i.fix_prompt })),
                                                      model: availableTemplates.find(t => t.id === fixTemplate)?.default_model || 'gpt-5.4-mini'
                                                    }),
                                                  })
                                                );
                                                const startData = await res.json();
                                                if (!startData.success) { setBatchFixingSection(null); setBatchFixProgress(null); return; }

                                                const pollInterval = setInterval(async () => {
                                                  try {
                                                    const statusRes = await fetch(
                                                      `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-batch-fix-status/${encodeURIComponent(key)}`,
                                                      withAuthFetchOptions()
                                                    );
                                                    const statusData = await statusRes.json();
                                                    if (statusData.status === 'fixing') {
                                                      setBatchFixProgress({ total: statusData.total, completed: statusData.completed });
                                                    } else if (statusData.status === 'done') {
                                                      clearInterval(pollInterval);
                                                      if (selectedTemplateRef.current === fixTemplate && iframeRef.current) {
                                                        iframeRef.current.src = buildIframeSrc(fixTemplate, Date.now());
                                                      }
                                                      setQaReportData(prev => {
                                                        if (!prev) return prev;
                                                        const newSections = { ...prev.sections };
                                                        let ptsAdded = 0;
                                                        const fixedImpIds = [];
                                                        const allRelatedIssueIds = [];

                                                        if (newSections[key]) {
                                                          const targetImps = (newSections[key].improvements || []).filter(i => !i._fixed);
                                                          ptsAdded = targetImps.reduce((sum, imp) => sum + (imp.estimated_points || 0), 0);
                                                          targetImps.forEach(i => {
                                                            fixedImpIds.push(i.id);
                                                            if (i.related_issue_ids) allRelatedIssueIds.push(...i.related_issue_ids);
                                                          });
                                                          newSections[key] = { ...newSections[key], improvements: (newSections[key].improvements || []).map(i => ({ ...i, _fixed: true })) };
                                                          newSections[key].score = Math.min(newSections[key].max || 20, (newSections[key].score || 0) + ptsAdded);
                                                        }
                                                        const newScore = Math.min(100, (prev.overall_score || 0) + ptsAdded);

                                                        // Sync with flat issues list
                                                        const newIssues = (prev.issues || []).map(issue => {
                                                          if (fixedImpIds.includes(issue.related_improvement_id) || allRelatedIssueIds.includes(issue.id)) {
                                                            return { ...issue, _fixed: true };
                                                          }
                                                          return issue;
                                                        });

                                                        const updatedReport = { ...prev, overall_score: newScore, sections: newSections, issues: newIssues };
                                                        
                                                        // Real-time success re-evaluation
                                                        const hasUnfixedMajor = updatedReport.issues.some(i => !i._fixed && (i.severity === 'critical' || i.severity === 'major'));
                                                        updatedReport.success = updatedReport.overall_score >= 80 && !hasUnfixedMajor;
                                                        
                                                        return updatedReport;
                                                      });
                                                      setBatchFixingSection(null);
                                                      setBatchFixProgress(null);
                                                      const sectionLabel = { educational_structure: 'Educational Structure', interactivity: 'Interactivity', ui_ux: 'UI/UX & Readability', images_and_media: 'Images & Media', content_accuracy: 'Content Accuracy' }[key] || key;
                                                      const fixedImprovements = sec.improvements.filter(i => !i._fixed);
                                                      const fixingCount = fixedImprovements.length;
                                                      const issuesList = fixedImprovements.slice(0, 3).map(i => `• ${i.description}`).join('\n') + (fixingCount > 3 ? `\n• ...and ${fixingCount - 3} more` : '');
                                                      
                                                      window.dispatchEvent(new CustomEvent('qaFixApplied', {
                                                        detail: { 
                                                          template: fixTemplate, 
                                                          userText: `Apply batch fixes for section: ${sectionLabel}.\n\nImprovements applying:\n${issuesList}`, 
                                                          advisorText: `Successfully applied ${statusData.completed || fixingCount} fixes for the ${sectionLabel} section.`
                                                        }
                                                      }));
                                                      window.dispatchEvent(new CustomEvent('qa_fix_applied'));
                                                    } else if (statusData.status === 'error') {
                                                      clearInterval(pollInterval);
                                                      setBatchFixingSection(null);
                                                      setBatchFixProgress(null);
                                                    }
                                                  } catch { }
                                                }, 3000);
                                              } catch { setBatchFixingSection(null); setBatchFixProgress(null); }
                                            }}
                                          >
                                            {batchFixingSection === key ? (
                                              <><div className="deploy-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', display: 'inline-block', marginRight: '4px' }}></div>Fixing {Math.min((batchFixProgress?.completed || 0) + 1, batchFixProgress?.total || 1)}/{batchFixProgress?.total || '...'}...</>
                                            ) : `Fix All Section Issues (${sec.improvements.filter(i => !i._fixed).length})`}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {qaReportData.issues && qaReportData.issues.length > 0 && (
                        <div className="qa-section">
                          <h3
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
                            onClick={() => setIssuesCollapsed(prev => !prev)}
                          >
                            <span style={{ transition: 'transform 0.2s', transform: issuesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
                            Issues Found ({qaReportData.issues.length})
                          </h3>
                          <div className="qa-issues-list" style={{ display: issuesCollapsed ? 'none' : 'block' }}>
                            {qaReportData.issues.map((issue) => {
                              const isNew = qaReportData.diff_from_previous?.new_ids?.includes(issue.id);
                              const isPersisted = qaReportData.diff_from_previous?.persisted_ids?.includes(issue.id);
                              return (
                                <div key={issue.id} className={`qa-issue-card severity-${issue.severity}`} style={{ opacity: issue._fixed ? 0.6 : 1, position: 'relative' }}>
                                  <div className="qa-issue-header" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    <span className={`qa-severity-badge ${issue.severity}`}>
                                      {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                                    </span>
                                    {isNew && <span style={{ backgroundColor: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65em', fontWeight: 'bold' }}>NEW</span>}
                                    {isPersisted && <span style={{ backgroundColor: '#eab308', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65em', fontWeight: 'bold' }}>PERSISTED</span>}
                                    <span className="qa-issue-category">{issue.category ? issue.category.replace(/\|/g, " • ") : "Issue"}</span>
                                    {issue._fixed && (
                                      <span style={{ marginLeft: 'auto', backgroundColor: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.7em', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ fontSize: '1.1em' }}>✓</span> Resolved
                                      </span>
                                    )}
                                  </div>
                                  <p className="qa-issue-description" style={{ textDecoration: issue._fixed ? 'line-through' : 'none', color: issue._fixed ? 'var(--muted)' : 'inherit', marginTop: '8px' }}>{issue.description}</p>
                                  {fixIssueError != null && fixIssueError.issueId === issue.id && (
                                    <p className="qa-fix-error">⚠️ {fixIssueError.message}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {qaReportData.diff_from_previous && qaReportData.diff_from_previous.resolved?.length > 0 && (
                        <div className="qa-section" style={{ marginTop: '20px' }}>
                          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#22c55e' }}>
                            <span>✅</span> Resolved Since Last Run ({qaReportData.diff_from_previous.resolved.length})
                          </h3>
                          <div className="qa-issues-list">
                            {qaReportData.diff_from_previous.resolved.map((issue) => (
                              <div key={issue.id} className={`qa-issue-card`} style={{ opacity: 0.7, borderLeft: '4px solid #22c55e' }}>
                                <div className="qa-issue-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ textDecoration: 'line-through', fontSize: '0.85em', color: 'var(--muted)' }}>
                                    {issue.category ? issue.category.replace(/\|/g, " • ") : "Issue"}
                                  </span>
                                  <span style={{ marginLeft: 'auto', color: '#22c55e', fontSize: '0.75em', fontWeight: 'bold' }}>
                                    Resolved
                                  </span>
                                </div>
                                <p className="qa-issue-description" style={{ textDecoration: 'line-through', color: 'var(--muted)', marginTop: '8px' }}>{issue.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {qaReportData.turns !== undefined && (
                        <div className="qa-section">
                          <h3>Evaluation Details</h3>
                          <p className="qa-details-text">Steps taken: {qaReportData.turns}</p>
                          {qaReportData.evaluated_at && <p className="qa-details-text" style={{ fontSize: '0.85em', color: 'var(--muted, #94a3b8)' }}>Evaluated: {new Date(qaReportData.evaluated_at).toLocaleString()}</p>}
                        </div>
                      )}
                      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                          className="modal-button"
                          style={{ background: '#334155', color: '#e2e8f0' }}
                          // AFTER
onClick={async () => {
  const res = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/qa-report/pdf`, {
    headers: { Authorization: `Bearer ${currentToken}` }
  });
  if (!res.ok) {
    const err = await res.json();
    alert(err.error || 'Failed to download PDF');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `qa_report_${selectedTemplate}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}}
                        >
                          📄 Download PDF
                        </button>
                        {['manager', 'creator'].includes(currentLessonRole) && (
                          <button
                            className="modal-button primary"
                            onClick={async () => {
                              setIsQaLoading(true);
                              try {
                                await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
                                setQaReportData({ status: 'generating' });
                                startQaPolling(selectedTemplate);
                              } catch (err) {
                                setIsQaLoading(false);
                                setQaReportData({ status: 'error', error: "Failed to start evaluation." });
                              } 
                            }}
                          >
                            Regenerate QA Report
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p>No report data available.</p>
                  )}
                </div>
              </div>
            </div>
          )}

      {/* Code Preview Modal */}
      {showCodePreview && selectedTemplate && (
        <CodePreview
          templateId={selectedTemplate}
          isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
          onClose={() => setShowCodePreview(false)}
        />
      )}

          {/* Delete Template Confirmation Modal */}
          {showDeleteModal && templateToDelete && (
        <div className="modal-overlay" onClick={() => !isDeletingTemplate && handleDeleteCancel()}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Delete lesson</h2>
                  <button
                className="modal-close"
                onClick={handleDeleteCancel}
                disabled={isDeletingTemplate}
                aria-disabled={isDeletingTemplate}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {deleteStep === 1 ? (
                <>
                  <div className="delete-warning">
                    <div className="warning-icon">!</div>
                    <h3>Are you sure?</h3>
                    <p>You are about to delete <strong>"{templateToDelete.name}"</strong>.</p>
                    <p>This action cannot be undone.</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                    >
                      Yes, Delete
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="delete-warning step2">
                    <div className="warning-icon">⚠️</div>
                    <h3>Final Confirmation</h3>
                    <p>This is your last chance to cancel.</p>
                        <p>Deleting <strong>"{templateToDelete.name}"</strong> will permanently remove:</p>
                        <ul className="delete-consequences">
                          <li>The lesson file</li>
                          <li>All associated chat history</li>
                          <li>All saved data for this lesson</li>
                        </ul>
                    <p className="final-warning">Are you absolutely sure?</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                      disabled={isDeletingTemplate}
                    >
                      Cancel
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                      disabled={isDeletingTemplate}
                    >
                      {isDeletingTemplate ? (
                        <>
                          <span className="button-loading-dots" style={{ marginRight: '6px' }}>
                            <span></span><span></span><span></span>
                          </span>
                          Deleting...
                        </>
                      ) : (
                        'Delete Forever'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
     
{!galleryViewMode && selectedTemplate && !['reviewer', 'final_approver', 'distributor'].includes(currentLessonRole) && (
  <InlineEditor
  templateId={selectedTemplate}
  isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
  isEditMode={isEditMode}
  setIsEditMode={setIsEditMode}
  iframeRef={iframeRef}
  token={currentToken}
/>
)}

      </div> {/* main-pane */}
      {!isFullscreen && !chatMinimized && !chatDocked && !galleryViewMode && (
        <div
          className={`floating-chat ${chatMinimized ? 'minimized' : ''}`}
          style={{ left: 0, top: 0, transform: `translate(${chatPosition.x}px, ${chatPosition.y}px)` }}
        >
          <div
            className="floating-chat-header"
            onMouseDown={(e) => {
              chatDragRef.current.dragging = true;
              chatDragRef.current.offsetX = chatPosition.x - e.clientX;
              chatDragRef.current.offsetY = chatPosition.y - e.clientY;
            }}
          >
            <span className="floating-chat-title">Lesson Copilot</span>
            <div className="floating-chat-actions">
              <button
                className="icon-btn small"
                onClick={() => setChatMinimized(!chatMinimized)}
                title={chatMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                <span aria-hidden>{chatMinimized ? '■' : '▁'}</span>
                <span className="sr-only">{chatMinimized ? 'Expand chat' : 'Minimize chat'}</span>
              </button>
              <button
                className="icon-btn small"
                onClick={() => {
                  setChatDocked(true);
                  setChatCollapsed(false);
                  setChatMinimized(true);
                }}
                title="Dock chat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <line x1="9" y1="3" x2="9" y2="21"></line>
                </svg>
                <span className="sr-only">Dock chat</span>
              </button>
            </div>
          </div>
          {!chatMinimized && (
            <div className="floating-chat-body">
              <div className="floating-chat-content">
                {selectedTemplate ? (
                      <ChatAdvisor
                        key={`chat-${selectedTemplate}`}
                        selectedTemplate={selectedTemplate}
                        isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
                        isCreatingTemplate={isCreatingTemplate}
                        creatingTemplateDescription={creatingTemplateDescription}
                        pendingTemplateId={pendingTemplateId}
                        apiKeysConfig={apiKeysConfig}
                        defaultModelForTemplate={availableTemplates.find(t => t.id === selectedTemplate)?.default_model}
                      isEditMode={isEditMode}
                      setIsEditMode={setIsEditMode}
                      getAccessTokenSilently={getAccessTokenSilently}
                      />
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>It's empty</p>
                    <p>Select or create a lesson to chat with the copilot.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div> {/* layout-shell */}
    {currentRole === 'distributor' && (
  <DistributorDashboard
    token={currentToken}
    onAssignLearners={(lesson) => {
      setLearnerAssignLesson(lesson);
      setShowLearnerAssignModal(true);
    }}
    onDeploy={(lesson) => {
      setSelectedTemplate(lesson.template_id);
      setGalleryViewMode(false);
      // Trigger deploy
      setTimeout(() => handleDeployClick(), 500);
    }}
  />
)}

{showLearnerAssignModal && learnerAssignLesson && (
  <LearnerAssignModal
    lesson={learnerAssignLesson}
    token={currentToken}
    onClose={() => { setShowLearnerAssignModal(false); setLearnerAssignLesson(null); }}
  />
)}

    <MyTasks
  token={currentToken}
  role={currentRole}
  onStartLesson={(task) => {
    console.log('[MyTasks] Starting lesson for task:', task.title);
    setNewTemplateName(task.title);
    // Use description if available, otherwise fall back to the task title
    // so the user never faces a blank prompt field
    setNewTemplateDescription(task.description || task.title || '');
    // Pre-fill files from backlog
    if (task.reference_image_previews?.length > 0) {
      setNewTemplateImagePreviews(task.reference_image_previews);
      setNewTemplateImages(task.reference_image_previews.map((_, i) => ({ name: `ref_image_${i}` })));
    }
    if (task.data_file_path) {
      setNewTemplateFilePath(task.data_file_path);
      setNewTemplateFile({ name: task.data_file_path.split('/').pop() });
    }
    setNewTemplateIsAssessment(task.is_assessment || false);
    setNewTemplateAssessmentSource(task.assessment_source || 'topic');
    setNewTemplateAssessmentTopic(task.assessment_topic || '');
    setNewTemplateAssessmentTemplateIds((task.assessment_template_ids || []).join(', '));
    setNewTemplateQuestionCount(task.question_count ? String(task.question_count) : '');
    setShowAddTemplateModal(true);
    window.__pendingBacklogId = task.backlog_id;
  }}
/>


    </div>
  );
}
function LearnerAssignModal({ lesson, token, onClose }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  const [learnerUsers, setLearnerUsers] = useState([]);
  const [learnerIds, setLearnerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Fetch learner role users
    fetch(`${API_URL}/distributor/learners`, { headers })
      .then(r => r.json())
      .then(data => { if (data?.success) setLearnerUsers(data.users || []); })
      .catch(() => {});

    // Fetch existing assignments
    fetch(`${API_URL}/admin/lessons/${lesson.template_id}/learners`, { headers })
      .then(r => r.json())
      .then(data => { if (data.success) setLearnerIds(data.learner_ids || []); })
      .catch(() => {});
  }, [lesson.template_id, token]);

  const handleToggle = (userId) => {
    setLearnerIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${API_URL}/admin/lessons/${lesson.template_id}/learners`, {
      method: 'POST', headers,
      body: JSON.stringify({ learner_ids: learnerIds }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 1000);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '24px',
        width: '400px', maxHeight: '80vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700 }}>🎓 Assign Learners</h3>
        <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#64748b' }}>{lesson.name}</p>
        
        {learnerUsers.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '16px' }}>
            No learners found. Add users with the learner role first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
            {learnerUsers.map(user => (
              <div key={user.user_id}
                onClick={() => handleToggle(user.user_id)}
                style={{
                  padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  border: `2px solid ${learnerIds.includes(user.user_id) ? '#6366f1' : '#e2e8f0'}`,
                  background: learnerIds.includes(user.user_id) ? '#eef2ff' : '#f8fafc',
                }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                  background: learnerIds.includes(user.user_id) ? '#6366f1' : '#f1f5f9',
                  border: `2px solid ${learnerIds.includes(user.user_id) ? '#6366f1' : '#cbd5e1'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {learnerIds.includes(user.user_id) && (
                    <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>✓</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{user.name || user.email}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{user.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '10px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Saving...' : saved ? '✅ Saved!' : '💾 Save'}
          </button>
          <button onClick={onClose}
            style={{ padding: '10px 16px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function DistributorTeamPanel({ templateId, token }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  const [team, setTeam] = useState({});
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!templateId || !token) return;
    Promise.all([
      fetch(`${API_URL}/lesson/templates/${templateId}/assigned-team`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()),
      fetch(`${API_URL}/lesson/templates/${templateId}/review/status`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()),
    ]).then(([teamData, statusData]) => {
      if (teamData.success) setTeam(teamData.team);
      if (statusData.success) setStatus(statusData.review?.status);
    }).catch(() => {});
  }, [templateId, token]);

  const ROLE_COLORS = {
    creator: '#6366f1', reviewer: '#f59e0b',
    final_approver: '#10b981', distributor: '#3b82f6',
  };

  const STATUS_LABELS = {
    draft: { label: 'Draft', color: '#94a3b8' },
    in_review: { label: 'In Review', color: '#f59e0b' },
    changes_requested: { label: 'Changes Requested', color: '#ef4444' },
    final_review: { label: 'Final Review', color: '#8b5cf6' },
    approved: { label: 'Approved', color: '#10b981' },
    deployed: { label: 'Deployed', color: '#3b82f6' },
  };

  const statusInfo = STATUS_LABELS[status] || STATUS_LABELS.draft;

  return (
    <div style={{
      width: '240px', minWidth: '240px', height: '100%',
      background: '#f8fafc', borderLeft: '1px solid #e2e8f0',
      display: 'flex', flexDirection: 'column', fontSize: '13px',
    }}>
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid #e2e8f0',
        background: '#fff', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>Lesson Info</span>
        <span style={{
          fontSize: '11px', fontWeight: 600, color: '#fff',
          padding: '2px 8px', borderRadius: '20px',
          background: statusInfo.color,
        }}>
          {statusInfo.label}
        </span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>
          Team
        </div>
        {Object.keys(team).length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '12px' }}>No team assigned</div>
        ) : (
          Object.entries(team).map(([teamRole, members]) =>
            members.map(member => (
              <div key={member.user_id} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '8px',
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: ROLE_COLORS[teamRole] || '#6366f1',
                  color: '#fff', fontSize: '11px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {(member.name || member.email || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                    {member.name || member.email?.split('@')[0]}
                  </div>
                  <div style={{ fontSize: '10px', color: ROLE_COLORS[teamRole] || '#6366f1', fontWeight: 600, textTransform: 'capitalize' }}>
                    {teamRole.replace('_', ' ')}
                  </div>
                </div>
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
}

function AppRoot() {
  if (window.location.pathname === '/monitor-cost') return <MonitorCostPage />;
  return <App />;
}

export default AppRoot;
